import Phaser from 'phaser';
import { GRID, COLORS, PLAYER_STATS, ENEMY_DEFINITIONS, SKILLS, CLASS_DEFINITIONS } from '../constants';
import { getActiveSkillsForClass } from '../systems/SkillManager';
import { GridPosition, CombatantState, CombatPhase, EnemyType, SkillDefinition } from '../types/Combat.types';
import { ELITE_MODIFIERS, rollEliteModifier, isModifierRedundant, EliteModifierId } from '../data/eliteModifiers';
import { applyEnragedBonus, applyDefenderModifiers, calculateThornsDamage, applyReviveHook, calculateRegenHeal } from '../systems/CombatCalculations';
import { blood as fxBlood, explosion as fxExplosion, dust as fxDust, ensureParticleTexture } from '../rendering/ParticleEffects';
import { SfxManager } from '../utils/SfxManager';
import { InventoryManager } from '../systems/InventoryManager';
import { ITEM_DEFINITIONS, CONSUMABLE_DEFINITIONS } from '../data/items';
import { RunManager, CombatConfig } from '../systems/RunManager';
import { SurvivorClass } from '../systems/CampManager';
import { ACHIEVEMENTS } from '../systems/MetaManager';
// ConsumableInstance est utilisé via InventoryManager.getConsumables()
import { rollCombatNormalResources, rollCombatEliteResources, rollCombatBossResources, formatResourceDrop } from '../utils/ResourceLoot';
import { SpriteRenderer, Tier } from '../rendering/SpriteRenderer';
import { TutorialOverlay } from '../ui/TutorialOverlay';
import { CodexManager } from '../systems/CodexManager';
import { MusicManager } from '../systems/MusicManager';
import { TouchInputManager, LongPressEvent } from '../input/TouchInputManager';
import { isTouchDevice, isMobileOrTablet } from '../utils/DeviceUtils';
import { ensureTouchTarget, showInfoPanel } from '../ui/MobileTouchUI';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { setSceneCursor } from '../ui/ButtonPolish';
import { FONT_BODY } from '../utils/Typography';

// === SCÈNE DE COMBAT ===
// Grille 8x8 tour par tour. Gère 1 joueur vs N ennemis.
// Le joueur dispose de 3 compétences sélectionnables.

/** Type union pour les sprites de combattants (rectangle ou sprite SVG) */
type CombatSprite = Phaser.GameObjects.Rectangle | Phaser.GameObjects.Sprite;

/** Détermine le tier visuel d'un ennemi selon l'étage */
function getTierForFloor(floor: number, isElite: boolean, isBoss: boolean): Tier {
  if (isBoss) {
    if (floor >= 6) return 'legendary';
    if (floor >= 4) return 'elite';
    return 'normal';
  }
  if (isElite) {
    if (floor >= 5) return 'legendary';
    return 'elite';
  }
  if (floor >= 7) return 'legendary';
  if (floor >= 4) return 'elite';
  return 'normal';
}

/** Mapping classe jeu → id SpriteRenderer héros */
const CLASS_TO_HERO: Record<SurvivorClass, string> = {
  warrior: 'guerrier',
  ranger: 'tireur',
  medic: 'medecin',
};

export class CombatScene extends Phaser.Scene {
  private tiles: Phaser.GameObjects.Rectangle[][] = [];

  // Multi-persos : tableau de joueurs, player = alias vers l'actif
  private players: CombatantState[] = [];
  private player!: CombatantState;             // joueur actif (alias)
  private activePlayerIndex = 0;
  private playerSprites: Map<string, CombatSprite> = new Map();
  private playerEmojis: Map<string, Phaser.GameObjects.Text> = new Map();
  private playerHpBars: Map<string, Phaser.GameObjects.Rectangle> = new Map();

  private enemies: CombatantState[] = [];
  private playerSprite!: CombatSprite; // sprite du joueur actif (alias)
  private enemySprites: Map<string, CombatSprite> = new Map();

  private playerHpBar!: Phaser.GameObjects.Rectangle; // HP bar du joueur actif (alias)
  private enemyHpBars: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  private enemyStatTexts: Map<string, Phaser.GameObjects.Text> = new Map();

  private turnText!: Phaser.GameObjects.Text;
  private statsText!: Phaser.GameObjects.Text;
  private skillInfoText!: Phaser.GameObjects.Text;
  private logText!: Phaser.GameObjects.Text;
  private combatLogs: string[] = [];
  private combatLogText!: Phaser.GameObjects.Text;
  private bannerHpText!: Phaser.GameObjects.Text;
  private bannerHpBar!: Phaser.GameObjects.Rectangle;
  private bannerHpBarBg!: Phaser.GameObjects.Rectangle;
  private bannerPaText!: Phaser.GameObjects.Text;
  private bannerPmText!: Phaser.GameObjects.Text;
  private bannerNameText!: Phaser.GameObjects.Text;
  private tooltip!: Phaser.GameObjects.Text;
  private turnOrderIcons: Phaser.GameObjects.Text[] = [];

  private phase: CombatPhase = 'player_turn';
  private movableTiles: GridPosition[] = [];
  private attackableTiles: GridPosition[] = [];
  private isAnimating = false;
  private currentEnemyIndex = 0;

  // Système de compétences
  private activeSkills: SkillDefinition[] = [...SKILLS];
  private selectedSkill: SkillDefinition = SKILLS[0];
  private skillButtons: Phaser.GameObjects.Text[] = [];

  // Audio
  private sfx = new SfxManager();
  // Input tactile
  private touchInput!: TouchInputManager;
  // Dernier drop de ressources (pour affichage victoire)
  private _lastResourceDrop: Partial<import('../systems/ResourceManager').ResourceStock> = {};
  // Éléments UI des consommables (pour pouvoir les redessiner)
  private consumableElements: Phaser.GameObjects.GameObject[] = [];
  private consumableBarPos = { x: 0, y: 0, w: 0 };

  // Flags anti-AoO (knockback ne déclenche pas d'AoO)
  private isKnockbackMove = false;

  // Debuffs actifs sur les combattants (immobilisation, etc.)
  private debuffs: Map<string, Set<string>> = new Map();
  // Indicateurs visuels de debuffs sur les sprites (emojis sous le sprite)
  private debuffIndicators: Map<string, Phaser.GameObjects.Text> = new Map();

  // Run et inventaire
  private run!: RunManager;
  private inventory!: InventoryManager;
  private combatConfig!: CombatConfig;

  // Mode ciblage pour les consommables damage+aoe (Sprint 83)
  // Si non-null : la prochaine onTileClick lance le consommable centré sur la case cliquée.
  private pendingThrow: { uid: string; throwRange: number; aoeRadius: number; damage: number; throwTiles: GridPosition[] } | null = null;
  private throwTargetingElements: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super({ key: 'CombatScene' });
  }

  init(data: { run?: RunManager }): void {
    this.run = data?.run ?? new RunManager();
    this.inventory = this.run.getInventory();
    this.combatConfig = this.run.getCurrentCombatConfig();
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    // Curseur croix dans CombatScene (feel de ciblage)
    setSceneCursor(this, 'crosshair');
    // Musique combat ou boss
    const isBoss = this.combatConfig.isBoss;
    MusicManager.crossfadeTo(this, isBoss ? 'boss' : 'combat');

    // Texture des particules (idempotent — ne fait rien si déjà créée)
    ensureParticleTexture(this);

    // Initialisation input tactile
    this.touchInput = new TouchInputManager(this);

    this.initCombatants();
    this.drawAtmosphere();
    this.drawGrid();
    this.drawCombatants();
    // Charger les skills (base + classe) AVANT drawUI : sinon la barre de skills
    // est dessinée avec 3 boutons figés (BASE_SKILLS) et le skill de classe
    // n'apparaît jamais dès la 1re partie (Bug 2).
    this.refreshActiveSkillsForCurrentPlayer();
    this.drawUI();
    this.selectSkill(0);

    // Sur mobile, long press sur la grille → afficher info de la case/entité
    if (isTouchDevice()) {
      this.events.on('longpress', (e: LongPressEvent) => {
        this.handleLongPress(e.x, e.y);
      });
    }

    // Tuto combat au premier run
    const stats = this.run.getMeta().getStats();
    const combatTutoKey = 'deadtide_combat_tuto_done';
    const combatTutoDone = localStorage.getItem(combatTutoKey) === '1';

    if (stats.totalRuns === 0 && !combatTutoDone) {
      const gridRight = GRID.ORIGIN_X + GRID.COLS * GRID.TILE_SIZE;
      const tuto = new TutorialOverlay(this, [
        {
          text: '⚔️ Bienvenue en combat !\n\nVotre équipe est à gauche,\nles ennemis à droite.',
          x: 300, y: 200,
        },
        {
          text: '🎯 Choisissez un skill en bas\npuis cliquez sur une case verte\npour vous déplacer ou rouge\npour attaquer.',
          x: 300, y: 450,
          arrowTo: { x: 300, y: GRID.ORIGIN_Y + GRID.ROWS * GRID.TILE_SIZE + 45 },
        },
        {
          text: '⚡ PA = Points d\'Action (attaques)\n👟 PM = Points de Mouvement\n\nLe bandeau sous la grille montre\nvos PA et PM restants.',
          x: 300, y: 250,
          arrowTo: { x: 300, y: GRID.ORIGIN_Y + GRID.ROWS * GRID.TILE_SIZE + 18 },
        },
        {
          text: '📋 Le panel à droite montre\nles stats de votre équipe\net des ennemis.\n\nCliquez "Fin tour" quand\nvous avez fini.',
          x: 400, y: 250,
          arrowTo: { x: gridRight + 80, y: 100 },
        },
      ], () => {
        try { localStorage.setItem(combatTutoKey, '1'); } catch { /* */ }
        this.startPlayerTurn();
      });
      tuto.start();
    } else {
      this.startPlayerTurn();
    }
  }

  // ===========================
  // INITIALISATION
  // ===========================

  private initCombatants(): void {
    this.players = [];
    this.playerSprites.clear();
    this.playerEmojis.clear();
    this.playerHpBars.clear();

    const team = this.run.getTeam();
    // LOT C5 : positions de spawn groupées et centrées (avant : rows 4/2/6
    // → 4 cases d'écart, persos "vachement éloignés"). Maintenant adjacents
    // autour du centre vertical de la grille (rows 3/4/5 sur 8 rows).
    const teamCount = team.length > 0 ? team.length : 1;
    const centerRow = Math.floor(GRID.ROWS / 2);
    let playerSpawns: { col: number; row: number }[];
    if (teamCount === 1) {
      playerSpawns = [{ col: 1, row: centerRow }];
    } else if (teamCount === 2) {
      // 2 persos : adjacent, juste au-dessus et au-dessous du centre
      playerSpawns = [
        { col: 1, row: centerRow },
        { col: 1, row: centerRow - 1 },
      ];
    } else {
      // 3+ persos : 3 cases adjacentes centrées sur centerRow
      playerSpawns = [
        { col: 1, row: centerRow },
        { col: 1, row: centerRow - 1 },
        { col: 1, row: centerRow + 1 },
      ];
    }

    if (team.length > 0) {
      // Mode multi-persos
      for (let i = 0; i < team.length; i++) {
        const member = team[i];
        if (!member.alive) continue;
        const classDef = CLASS_DEFINITIONS[member.class];
        const baseStats = { ...classDef.stats };
        // L'équipement individuel s'applique à chaque joueur
        const computedStats = this.inventory.computeStats(member.id, baseStats);
        // HP persistant
        if (member.hp > 0 && member.hp < computedStats.maxHp) {
          computedStats.hp = member.hp;
        }
        this.players.push({
          id: member.id,
          name: member.name,
          stats: computedStats,
          position: { ...playerSpawns[i % playerSpawns.length] },
          currentPa: computedStats.pa,
          currentPm: computedStats.pm,
          isPlayer: true,
          isAlive: true,
        });
      }
    } else {
      // Fallback : un seul joueur (rétro-compat)
      const classDef = CLASS_DEFINITIONS[this.run.getSelectedClass()];
      const baseStats = classDef ? { ...classDef.stats } : { ...PLAYER_STATS };
      const computedStats = this.inventory.computeStats('player', baseStats);
      const savedHp = this.run.getPlayerHp();
      if (savedHp > 0) {
        computedStats.hp = Math.min(savedHp, computedStats.maxHp);
      }
      this.players.push({
        id: 'player',
        name: this.run.getSelectedSurvivor()?.name ?? 'Capitaine',
        stats: computedStats,
        position: { col: 1, row: 4 },
        currentPa: computedStats.pa,
        currentPm: computedStats.pm,
        isPlayer: true,
        isAlive: true,
      });
    }

    // Le premier joueur vivant est l'actif
    this.activePlayerIndex = 0;
    this.player = this.players[0];

    const spawns = this.combatConfig.enemies;

    this.enemies = [];
    this.enemySprites.clear();
    this.enemyHpBars.clear();

    // Scaling : +15% HP/étage dès étage 2, +1 ATK/étage dès étage 2, +0.5 DEF/étage dès étage 4
    // Après le 1er boss (étage 7+), on ajoute PM (+1 à 7, +2 à 10) et PA (+1 à 9)
    // sur les ennemis non-boss, pour varier la difficulté au-delà du simple sac à HP.
    const currentNode = this.run.getMap().nodes.find(n => n.id === this.run.getMap().currentNodeId);
    const floor = currentNode?.floor ?? 0;
    const nodeTypeHere = this.run.getCurrentNodeType();
    const hpScale = floor >= 2 ? 1 + (floor - 1) * 0.15 : 1;
    const atkBonus = floor >= 2 ? floor - 1 : 0;
    const defBonus = floor >= 4 ? Math.floor((floor - 3) * 0.5) : 0;
    const pmBonus = floor >= 10 ? 2 : (floor >= 7 ? 1 : 0);
    const paBonus = floor >= 9 ? 1 : 0;

    // Modificateurs d'élite : actifs uniquement sur les nœuds `elite` à partir de l'étage 5 (Acte 2+)
    const rollsModifiers = nodeTypeHere === 'elite' && floor >= 5;

    for (let i = 0; i < spawns.length; i++) {
      const spawn = spawns[i];
      const def = ENEMY_DEFINITIONS[spawn.type];
      const isBossSpawn = spawn.type.startsWith('boss');
      const scaledStats = { ...def.stats };
      scaledStats.hp = Math.round(scaledStats.hp * hpScale);
      scaledStats.maxHp = Math.round(scaledStats.maxHp * hpScale);
      scaledStats.attack = scaledStats.attack + atkBonus;
      scaledStats.defense = scaledStats.defense + defBonus;
      // Les boss ont déjà leurs stats dédiées — pas de bonus mobilité/actions supplémentaires
      if (!isBossSpawn) {
        scaledStats.pm = scaledStats.pm + pmBonus;
        scaledStats.pa = scaledStats.pa + paBonus;
      }

      // Tirage du modificateur (1 max par ennemi, évite les redondances avec les passifs existants)
      let modifier: EliteModifierId | null = null;
      if (rollsModifiers && !isBossSpawn) {
        for (let attempt = 0; attempt < 3; attempt++) {
          const candidate = rollEliteModifier();
          if (!candidate || !isModifierRedundant(spawn.type, candidate)) {
            modifier = candidate;
            break;
          }
        }
      }

      this.enemies.push({
        id: `${spawn.type}_${i}`,
        name: def.name,
        stats: scaledStats,
        position: { col: spawn.col, row: spawn.row },
        currentPa: scaledStats.pa,
        currentPm: scaledStats.pm,
        isPlayer: false,
        isAlive: true,
        enemyType: spawn.type,
        eliteModifier: modifier,
      });
    }
  }

  // ===========================
  // DESSIN GRILLE
  // ===========================

  /**
   * Atmosphère contextuelle : vignette colorée + particules ambiantes selon le type
   * de combat. Renforce la lecture immédiate (normal = neutre, élite = danger,
   * boss = climax). Tous les éléments en depth ≤ -1 pour rester derrière la grille.
   */
  private drawAtmosphere(): void {
    const nodeType = this.run.getCurrentNodeType();
    const isElite = nodeType === 'elite';
    const isBoss = this.combatConfig.isBoss || nodeType === 'boss';

    // Ton de fond global : neutre / rouge sombre / rouge sang
    let tintColor = 0x16213e; // base navy (couleur par défaut Phaser)
    let tintAlpha = 0;
    if (isBoss) {
      tintColor = 0x3a0a0a;
      tintAlpha = 0.45;
    } else if (isElite) {
      tintColor = 0x2a0a14;
      tintAlpha = 0.30;
    }
    if (tintAlpha > 0) {
      this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, tintColor, tintAlpha).setDepth(-10);
    }

    // Vignette : 4 dégradés sombres dans les coins (effet "tunnel vision")
    if (isElite || isBoss) {
      const corners = [
        { x: 0, y: 0 },
        { x: GAME_W, y: 0 },
        { x: 0, y: GAME_H },
        { x: GAME_W, y: GAME_H },
      ];
      for (const c of corners) {
        const v = this.add.circle(c.x, c.y, Math.max(GAME_W, GAME_H) * 0.5, 0x000000, isBoss ? 0.55 : 0.40);
        v.setDepth(-9);
      }
    }

    // Particules ambiantes : poussière qui flotte (combat normal) ou braises (boss)
    const particleColor = isBoss ? 0xff6b3d : 0x8a8a8a;
    const particleCount = isBoss ? 12 : 6;
    for (let i = 0; i < particleCount; i++) {
      const px = Phaser.Math.Between(20, GAME_W - 20);
      const py = Phaser.Math.Between(20, GAME_H - 20);
      const dot = this.add.circle(px, py, isBoss ? 1.5 : 1, particleColor, 0.5).setDepth(-8);
      // Flotte vers le haut puis se réinitialise en bas
      this.tweens.add({
        targets: dot,
        y: -10,
        alpha: 0,
        duration: Phaser.Math.Between(4000, 7000),
        delay: Phaser.Math.Between(0, 3000),
        repeat: -1,
        onRepeat: () => {
          dot.y = GAME_H + 10;
          dot.alpha = 0.5;
          dot.x = Phaser.Math.Between(20, GAME_W - 20);
        },
      });
    }

    // Boss : éclair sporadique (toutes les ~6-10s)
    if (isBoss) {
      const flashLightning = () => {
        const flash = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0xffffff, 0.18).setDepth(-7);
        this.tweens.add({
          targets: flash,
          alpha: 0,
          duration: 220,
          ease: 'Power2',
          onComplete: () => flash.destroy(),
        });
        this.time.delayedCall(Phaser.Math.Between(6000, 10000), flashLightning);
      };
      this.time.delayedCall(Phaser.Math.Between(3000, 6000), flashLightning);
    }
  }

  private drawGrid(): void {
    this.tiles = [];

    for (let row = 0; row < GRID.ROWS; row++) {
      this.tiles[row] = [];
      for (let col = 0; col < GRID.COLS; col++) {
        const x = GRID.ORIGIN_X + col * GRID.TILE_SIZE;
        const y = GRID.ORIGIN_Y + row * GRID.TILE_SIZE;
        const color = (row + col) % 2 === 0 ? COLORS.TILE_LIGHT : COLORS.TILE_DARK;

        const tile = this.add.rectangle(
          x + GRID.TILE_SIZE / 2,
          y + GRID.TILE_SIZE / 2,
          GRID.TILE_SIZE - 2,
          GRID.TILE_SIZE - 2,
          color
        );

        tile.setInteractive({ useHandCursor: true });

        tile.on('pointerover', () => {
          if (this.phase !== 'player_turn' || this.isAnimating) return;
          if (this.isTileHighlighted(col, row)) {
            tile.setFillStyle(this.getHighlightColor(col, row));
          } else {
            tile.setFillStyle(COLORS.TILE_HOVER);
          }
        });

        tile.on('pointerout', () => {
          this.refreshTileColor(col, row);
        });

        tile.on('pointerdown', () => {
          this.onTileClick(col, row);
        });

        this.tiles[row][col] = tile;
      }
    }
  }

  // ===========================
  // DESSIN COMBATTANTS
  // ===========================

  private drawCombatants(): void {
    // Déterminer le tier visuel selon l'étage
    const currentNode = this.run.getMap().nodes.find(n => n.id === this.run.getMap().currentNodeId);
    const floor = currentNode?.floor ?? 0;
    const nodeType = this.run.getCurrentNodeType();
    const isElite = nodeType === 'elite';
    const isBossNode = nodeType === 'boss';

    // Dessiner tous les joueurs (sprites SVG)
    for (const p of this.players) {
      const classDef = CLASS_DEFINITIONS[this.getPlayerClass(p)];
      const pPos = this.gridToPixel(p.position);
      // Le capitaine a un sprite unique, les autres utilisent leur classe
      const heroId = p.id === 'captain' ? 'capitaine' : (CLASS_TO_HERO[this.getPlayerClass(p)] ?? 'guerrier');
      const textureKey = SpriteRenderer.heroKey(heroId);

      let sprite: CombatSprite;
      if (this.textures.exists(textureKey)) {
        sprite = this.add.sprite(pPos.x, pPos.y, textureKey);
        const scale = (GRID.TILE_SIZE - 12) / sprite.width;
        sprite.setScale(scale);
      } else {
        // Fallback rectangle si texture manquante
        const rect = this.add.rectangle(pPos.x, pPos.y, GRID.TILE_SIZE - 12, GRID.TILE_SIZE - 12, classDef?.color ?? COLORS.PLAYER);
        rect.setStrokeStyle(2, classDef?.outline ?? COLORS.PLAYER_OUTLINE);
        sprite = rect;
      }
      this.playerSprites.set(p.id, sprite);
      this.applyIdleBob(sprite);
      this.applyHpTint(sprite, p);

      // Emoji de classe au-dessus (conservé pour lisibilité)
      const emojiText = this.add.text(pPos.x, pPos.y - GRID.TILE_SIZE / 2 - 6, classDef?.emoji ?? '🏴‍☠️', {
        fontSize: '12px', fontFamily: FONT_BODY,
      }).setOrigin(0.5);
      this.playerEmojis.set(p.id, emojiText);
    }

    // Alias pour rétro-compat
    this.playerSprite = this.playerSprites.get(this.player.id)!;

    for (const enemy of this.enemies) {
      const def = ENEMY_DEFINITIONS[enemy.enemyType!];
      const ePos = this.gridToPixel(enemy.position);
      const isBoss = enemy.enemyType!.startsWith('boss');
      const tier = getTierForFloor(floor, isElite, isBoss || isBossNode);
      const textureKey = SpriteRenderer.enemyKey(enemy.enemyType!, tier);

      let sprite: CombatSprite;
      if (this.textures.exists(textureKey)) {
        sprite = this.add.sprite(ePos.x, ePos.y, textureKey);
        const targetSize = isBoss ? GRID.TILE_SIZE - 4 : GRID.TILE_SIZE - 12;
        const scale = targetSize / sprite.width;
        sprite.setScale(scale);
      } else {
        // Fallback rectangle si texture manquante
        const rect = this.add.rectangle(ePos.x, ePos.y, GRID.TILE_SIZE - 12, GRID.TILE_SIZE - 12, def.color);
        rect.setStrokeStyle(2, def.outline);
        sprite = rect;
      }
      sprite.setInteractive({ useHandCursor: true });

      // Infobulle : survol sur desktop, tap bref sur mobile (long press → panel détaillé)
      const buildTooltipText = () => {
        if (!enemy.isAlive) return '';
        const rawDmg = Math.max(1, this.player.stats.attack - enemy.stats.defense);
        const skillDmg = this.selectedSkill ? Math.max(1, Math.round(rawDmg * this.selectedSkill.damage)) : rawDmg;
        const passifs: Record<string, string> = {
          zombie: '🧟 Meute: +1 ATK/zombie adjacent',
          brute: '⛓️ Immobilisation: -1 PM cible',
          crab: '🦀 Étreinte: bloque la fuite',
          shark: '🦈 Flair: +2 ATK si cible <50% HP',
          siren: '🧜 Chant: soigne alliés',
          bomber: '💥 Explosion: 2 dégâts zone à la mort',
          cursed: '🌊 Aura: -1 ATK joueurs proches',
          fast: '👻 Fantôme: traverse les alliés',
        };
        const passifLine = passifs[enemy.enemyType ?? ''] ? `\n${passifs[enemy.enemyType ?? '']}` : '';
        // Modificateur d'élite (persistant, affiché séparément)
        const modLine = enemy.eliteModifier
          ? `\n${ELITE_MODIFIERS[enemy.eliteModifier].emoji} ${ELITE_MODIFIERS[enemy.eliteModifier].name}: ${ELITE_MODIFIERS[enemy.eliteModifier].description}`
          : '';
        return `${def.emoji} ${enemy.name}\n` +
          `HP: ${enemy.stats.hp}/${enemy.stats.maxHp}\n` +
          `ATK: ${enemy.stats.attack}  DEF: ${enemy.stats.defense}\n` +
          `→ ~${skillDmg} dégâts` + passifLine + modLine;
      };

      if (!isTouchDevice()) {
        // Desktop : hover classique
        sprite.on('pointerover', (pointer: Phaser.Input.Pointer) => {
          const text = buildTooltipText();
          if (!text) return;
          this.tooltip.setText(text);
          this.tooltip.setPosition(
            Math.min(pointer.x + 12, GAME_W - this.tooltip.width - 4),
            Math.max(pointer.y - 10, 4),
          );
          this.tooltip.setVisible(true);
        });
        sprite.on('pointermove', (pointer: Phaser.Input.Pointer) => {
          this.tooltip.setPosition(
            Math.min(pointer.x + 12, GAME_W - this.tooltip.width - 4),
            Math.max(pointer.y - 10, 4),
          );
        });
        sprite.on('pointerout', () => {
          this.tooltip.setVisible(false);
        });
      }
      sprite.on('pointerdown', () => {
        // Sur mobile, afficher brièvement le tooltip au tap
        if (isTouchDevice() && enemy.isAlive) {
          const text = buildTooltipText();
          if (text) {
            const ePixel = this.gridToPixel(enemy.position);
            this.tooltip.setText(text);
            this.tooltip.setPosition(
              Math.min(ePixel.x + 40, GAME_W - this.tooltip.width - 4),
              Math.max(ePixel.y - 30, 4),
            );
            this.tooltip.setVisible(true);
            this.time.delayedCall(2000, () => this.tooltip.setVisible(false));
          }
        }
        this.onTileClick(enemy.position.col, enemy.position.row);
      });

      this.enemySprites.set(enemy.id, sprite);
      this.applyIdleBob(sprite);

      // Étiquette nom au-dessus du sprite
      const nameTag = this.add.text(ePos.x, ePos.y - GRID.TILE_SIZE / 2 - 4, def.emoji, {
        fontSize: '10px', fontFamily: FONT_BODY,
      }).setOrigin(0.5).setDepth(1);
      nameTag.setName(`enemy_name_${enemy.id}`);
    }

    // Créer un indicateur de debuffs (texte vide au départ) pour chaque combattant
    for (const p of this.players) {
      const pPos = this.gridToPixel(p.position);
      const ind = this.add.text(pPos.x, pPos.y + GRID.TILE_SIZE / 2 - 2, '', {
        fontSize: '10px', fontFamily: FONT_BODY,
      }).setOrigin(0.5, 0).setDepth(5);
      this.debuffIndicators.set(p.id, ind);
    }
    for (const enemy of this.enemies) {
      const ePos = this.gridToPixel(enemy.position);
      const ind = this.add.text(ePos.x, ePos.y + GRID.TILE_SIZE / 2 - 2, '', {
        fontSize: '10px', fontFamily: FONT_BODY,
      }).setOrigin(0.5, 0).setDepth(5);
      this.debuffIndicators.set(enemy.id, ind);
    }
  }

  // ===========================
  // UI
  // ===========================

  private drawUI(): void {
    const _mob = isMobileOrTablet();
    // Mobile portrait : panel sous la grille au lieu d'à droite
    const gridBottom = GRID.ORIGIN_Y + GRID.ROWS * GRID.TILE_SIZE;
    const uiX = _mob ? 10 : GRID.ORIGIN_X + GRID.COLS * GRID.TILE_SIZE + 20;
    const panelWidth = _mob ? GAME_W - 20 : 230;
    const panelTopY = _mob ? gridBottom + 100 : 0;

    // Fond du panel
    if (_mob) {
      // Mobile : panel sous la grille, pleine largeur
      const panelH = GAME_H - panelTopY;
      this.add.rectangle(GAME_CX, panelTopY + panelH / 2, GAME_W, panelH, 0x111827, 0.6);
    } else {
      this.add.rectangle(uiX + panelWidth / 2 - 10, 350, panelWidth + 10, 700, 0x111827, 0.6);
    }

    const nodeType = this.run.getCurrentNodeType();
    const floorInfo = this.run.getCurrentFloorLabel();
    const combatLabel = this.combatConfig.isBoss
      ? '👹 BOSS'
      : nodeType === 'elite'
        ? `💀 ÉLITE — ${floorInfo}`
        : `⚔️ Combat — ${floorInfo}`;
    // Sprint 3 mobile (G1) : sur mobile le panel n'a que 275 px de hauteur
    // (525-800). On compacte en réduisant la hauteur des blocs joueurs
    // (44→28) et ennemis (48→28), masquant le statsText et la frise label
    // "Combat", et déplaçant le stats inline à droite de la mini barre HP.
    if (!_mob) {
      this.add.text(uiX, panelTopY + 15, combatLabel, {
        fontSize: '14px', color: COLORS.TEXT_GOLD, fontFamily: FONT_BODY, fontStyle: 'bold',
      });
    }

    this.turnText = this.add.text(uiX, panelTopY + (_mob ? 15 : 32), '', {
      fontSize: _mob ? '10px' : '13px', color: COLORS.TEXT_WHITE, fontFamily: FONT_BODY,
    });

    // --- JOUEURS ---
    let pY = panelTopY + (_mob ? 32 : 65);
    const playerBlockH = _mob ? 28 : 44;
    const playerBarW = _mob ? 70 : 110;
    const playerBarH = _mob ? 5 : 7;
    for (const p of this.players) {
      const cls = CLASS_DEFINITIONS[this.getPlayerClass(p)];
      // Cadre de fond pour chaque joueur (sert de highlight pour le joueur actif)
      const playerBg = this.add.rectangle(uiX + panelWidth / 2 - 10, pY + playerBlockH / 2 - 4, panelWidth + 4, playerBlockH - 2, 0x111827);
      playerBg.setStrokeStyle(p.id === this.player.id ? 2 : 0, 0xf1c40f);
      playerBg.setName(`player_bg_${p.id}`);

      const nameColor = p.id === this.player.id ? '#f1c40f' : '#3498db';
      this.add.text(uiX, pY + 1, `${cls.emoji} ${p.name}`, {
        fontSize: '11px', color: nameColor, fontFamily: FONT_BODY, fontStyle: 'bold',
      });
      pY += _mob ? 13 : 16;
      // LOT B2 : origine (0, 0.5) au lieu de (0.5) → la barre HP rétrécit
      // depuis la droite (gauche ancrée), comme attendu pour une barre de vie.
      // Avant : setOrigin(0.5) + setScale(ratio, 1) = barre qui rétrécit vers
      // le centre (visuellement bizarre : 50% HP affichait une demi-barre centrée).
      this.add.rectangle(uiX, pY + (_mob ? 4 : 0), playerBarW, playerBarH, COLORS.HP_BG).setOrigin(0, 0.5);
      const hpBar = this.add.rectangle(uiX, pY + (_mob ? 4 : 0), playerBarW, playerBarH, COLORS.HP_GREEN).setOrigin(0, 0.5);
      this.playerHpBars.set(p.id, hpBar);
      // Stats : sous la barre en desktop, inline (à droite de la mini-barre) en mobile
      const pStatX = _mob ? uiX + playerBarW + 6 : uiX;
      const pStatY = _mob ? pY - 1 : pY + 6;
      const pStatText = this.add.text(pStatX, pStatY, `HP:${p.stats.hp}/${p.stats.maxHp} PA:${p.currentPa} PM:${p.currentPm}`, {
        fontSize: _mob ? '9px' : '10px', color: '#8899aa', fontFamily: FONT_BODY,
      });
      pStatText.setName(`player_stat_${p.id}`);
      pY += _mob ? 15 : 28;
    }
    // Alias pour le joueur actif
    this.playerHpBar = this.playerHpBars.get(this.player.id)!;

    if (_mob) {
      // Mobile : statsText caché (info HP/PA/PM dans le bandeau, ATK/DEF/Portée
      // moins critique en jeu — info dispo via tooltip ennemi).
      this.statsText = this.add.text(0, 0, '').setVisible(false);
    } else {
      this.statsText = this.add.text(uiX, pY, '', {
        fontSize: '10px', color: COLORS.TEXT_WHITE, fontFamily: FONT_BODY, lineSpacing: 2,
      });
      pY += 35;
    }

    // --- ENNEMIS ---
    this.add.text(uiX, pY, '--- Ennemis ---', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    });

    let yOffset = pY + (_mob ? 14 : 18);
    const enemyBlockH = _mob ? 28 : 48;
    const enemyBarW = _mob ? 70 : 110;
    const enemyBarH = _mob ? 5 : 6;
    for (const enemy of this.enemies) {
      const def = ENEMY_DEFINITIONS[enemy.enemyType!];
      const colorHex = '#' + def.color.toString(16).padStart(6, '0');

      this.add.text(uiX, yOffset, `${def.emoji} ${enemy.name}`, {
        fontSize: '11px', color: colorHex, fontFamily: FONT_BODY, fontStyle: 'bold',
      });
      // Mobile : barre HP plus petite, ancrée à gauche, stats inline à droite.
      // Desktop : barre HP centrée sous le nom, stats sous la barre (inchangé).
      const eBarX = _mob ? uiX : uiX + 55;
      const eBarY = _mob ? yOffset + 18 : yOffset + 17;
      const eBarOrigin = _mob ? [0, 0.5] : [0.5, 0.5];
      this.add.rectangle(eBarX, eBarY, enemyBarW, enemyBarH, COLORS.HP_BG).setOrigin(eBarOrigin[0], eBarOrigin[1]);
      const hpBar = this.add.rectangle(eBarX, eBarY, enemyBarW, enemyBarH, COLORS.HP_GREEN).setOrigin(eBarOrigin[0], eBarOrigin[1]);
      this.enemyHpBars.set(enemy.id, hpBar);

      // Stats texte : sous la barre en desktop, inline à droite de la mini-barre en mobile
      const eStatX = _mob ? uiX + enemyBarW + 6 : uiX;
      const eStatY = _mob ? yOffset + 14 : yOffset + 24;
      const statText = this.add.text(eStatX, eStatY, '', {
        fontSize: _mob ? '9px' : '10px', color: '#8899aa', fontFamily: FONT_BODY,
      });
      this.enemyStatTexts.set(enemy.id, statText);

      yOffset += enemyBlockH;
    }

    // --- SKILL SÉLECTIONNÉ (masqué sur mobile, info via skill bar) ---
    if (!_mob) {
      this.add.text(uiX, yOffset + 5, '--- Skill actif ---', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      });
      this.skillInfoText = this.add.text(uiX, yOffset + 22, '', {
        fontSize: '10px', color: COLORS.TEXT_WHITE, fontFamily: FONT_BODY, lineSpacing: 2,
        wordWrap: { width: panelWidth - 10 },
      });
    } else {
      this.skillInfoText = this.add.text(0, 0, '').setVisible(false);
    }

    // --- CONSOMMABLES ---
    // En mobile, on les sort du panel (qui déborde déjà avec joueurs + ennemis)
    // pour les afficher en rangée horizontale fixe sous la barre de skills.
    // Sinon le panel droite/desktop reste vertical comme avant.
    if (_mob) {
      const gridBottom2 = GRID.ORIGIN_Y + GRID.ROWS * GRID.TILE_SIZE;
      this.drawConsumableBar(GRID.ORIGIN_X, gridBottom2 + 64, GRID.COLS * GRID.TILE_SIZE);
    } else {
      this.drawConsumableBar(uiX, yOffset + 80, panelWidth);
    }

    // --- FRISE D'ORDRE DES TOURS (au-dessus de la grille) ---
    this.drawTurnOrder();

    // --- BANDEAU JOUEUR (sous la grille) ---
    this.drawPlayerBanner();

    // --- BARRE DE SKILLS (sous le bandeau) ---
    this.drawSkillBar();

    // Log principal (sous les skills) — masqué sur mobile (overlap avec le
    // panel sous-grille à panelTopY=525, info redondante avec le bandeau).
    if (_mob) {
      this.logText = this.add.text(0, 0, '').setVisible(false);
    } else {
      this.logText = this.add.text(GRID.ORIGIN_X, gridBottom + 68, '', {
        fontSize: '10px', color: '#8899aa', fontFamily: FONT_BODY, wordWrap: { width: 500 },
      });
    }

    // Historique des actions
    this.combatLogs = [];
    if (!_mob) {
      // Desktop : affiché à droite dans le panel
      const logX = GRID.ORIGIN_X + GRID.COLS * GRID.TILE_SIZE + 20;
      this.combatLogText = this.add.text(logX, 580, '', {
        fontSize: '10px', color: '#8899aa', fontFamily: FONT_BODY,
        wordWrap: { width: 220 }, lineSpacing: 1,
      });
    } else {
      // Mobile : masqué (espace limité, info via log principal)
      this.combatLogText = this.add.text(0, 0, '').setVisible(false);
    }

    // Tooltip (caché par défaut, s'affiche au survol ennemi)
    this.tooltip = this.add.text(0, 0, '', {
      fontSize: '10px', color: '#ffffff', fontFamily: FONT_BODY,
      backgroundColor: '#000000', padding: { x: 6, y: 4 },
    });
    this.tooltip.setDepth(100);
    this.tooltip.setVisible(false);

    // Raccourcis clavier
    this.input.keyboard!.on('keydown-SPACE', () => {
      if (this.phase === 'player_turn' && !this.isAnimating) this.endPlayerTurn();
    });
    this.input.keyboard!.on('keydown-ONE', () => this.selectSkill(0));
    this.input.keyboard!.on('keydown-TWO', () => this.selectSkill(1));
    this.input.keyboard!.on('keydown-THREE', () => this.selectSkill(2));
    // Touche 4 = skill de classe (seulement si présent, sinon selectSkill no-op)
    this.input.keyboard!.on('keydown-FOUR', () => this.selectSkill(3));

    // (bouton fin de tour déplacé dans le bandeau joueur)
  }

  /** Bandeau joueur actif : HP bar, PA, PM, bouton fin de tour — sous la grille */
  private drawPlayerBanner(): void {
    const bannerY = GRID.ORIGIN_Y + GRID.ROWS * GRID.TILE_SIZE + 4;
    const gridWidth = GRID.COLS * GRID.TILE_SIZE;
    const cls = CLASS_DEFINITIONS[this.getPlayerClass(this.player)];

    // Fond du bandeau
    this.add.rectangle(GRID.ORIGIN_X + gridWidth / 2, bannerY + 14, gridWidth, 30, 0x111827)
      .setStrokeStyle(1, 0x2a3a5c);

    // Nom du joueur actif
    this.bannerNameText = this.add.text(GRID.ORIGIN_X + 4, bannerY + 3, `${cls.emoji} ${this.player.name}`, {
      fontSize: '10px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    });

    // Barre HP — plus courte en mobile pour faire de la place à PA/PM/bouton fin tour
    const mobBanner = isMobileOrTablet();
    const hpBarX = GRID.ORIGIN_X + (mobBanner ? 75 : 130);
    const hpBarW = mobBanner ? 70 : 120;
    this.bannerHpBarBg = this.add.rectangle(hpBarX, bannerY + 14, hpBarW, 10, COLORS.HP_BG).setOrigin(0, 0.5);
    this.bannerHpBar = this.add.rectangle(hpBarX, bannerY + 14, hpBarW, 10, COLORS.HP_GREEN).setOrigin(0, 0.5);
    this.bannerHpText = this.add.text(hpBarX + hpBarW + 4, bannerY + 5, '', {
      fontSize: '10px', color: '#ecf0f1', fontFamily: FONT_BODY,
    });

    // PA et PM bien en évidence — repositionnés en mobile pour ne plus chevaucher
    // ni le texte HP (à gauche) ni le bouton "Fin tour" (à droite).
    const paX = mobBanner ? GRID.ORIGIN_X + 200 : GRID.ORIGIN_X + 310;
    const pmX = mobBanner ? GRID.ORIGIN_X + 245 : GRID.ORIGIN_X + 380;
    this.bannerPaText = this.add.text(paX, bannerY + 2, '', {
      fontSize: '13px', color: '#f39c12', fontFamily: FONT_BODY, fontStyle: 'bold',
    });
    this.bannerPmText = this.add.text(pmX, bannerY + 2, '', {
      fontSize: '13px', color: '#3498db', fontFamily: FONT_BODY, fontStyle: 'bold',
    });

    // Bouton fin de tour intégré au bandeau (à droite). Label compact en mobile
    // pour rester dans les ~80px restants après PM.
    const endTurnLabel = mobBanner ? '⏭ Fin' : '⏭ Fin tour';
    const endTurnBtn = this.add.text(GRID.ORIGIN_X + gridWidth - 4, bannerY + 3, endTurnLabel, {
      fontSize: '10px', color: '#f39c12', fontFamily: FONT_BODY,
      backgroundColor: '#2c3e50', padding: { x: 6, y: 3 },
    }).setOrigin(1, 0);
    endTurnBtn.setInteractive({ useHandCursor: true });
    endTurnBtn.on('pointerdown', () => {
      if (this.phase === 'player_turn' && !this.isAnimating) this.endPlayerTurn();
    });
    endTurnBtn.on('pointerover', () => endTurnBtn.setColor('#f1c40f'));
    endTurnBtn.on('pointerout', () => endTurnBtn.setColor('#f39c12'));
    ensureTouchTarget(endTurnBtn);
  }

  private drawSkillBar(): void {
    const barY = GRID.ORIGIN_Y + GRID.ROWS * GRID.TILE_SIZE + 38;
    this.skillButtons = [];

    const gridWidth = GRID.COLS * GRID.TILE_SIZE;
    const btnWidth = Math.floor(gridWidth / this.activeSkills.length) - 4;

    for (let i = 0; i < this.activeSkills.length; i++) {
      const skill = this.activeSkills[i];
      const btnX = GRID.ORIGIN_X + i * (btnWidth + 4);
      const canAfford = this.player.currentPa >= skill.paCost;
      // Label sans préfixe ⚠ — la couleur (rouge si insuffisant) et le shake
      // suffisent à signaler. Le ⚠ ajoutait 1 char qui faisait déborder le texte.
      const paLabel = `${skill.paCost}PA`;

      const btn = this.add.text(btnX, barY, `${skill.emoji}${skill.name} ${paLabel}`, {
        fontSize: '10px',
        color: canAfford ? '#bdc3c7' : '#e74c3c',
        fontFamily: FONT_BODY,
        backgroundColor: canAfford ? '#2c3e50' : '#1a1a2e',
        padding: { x: 6, y: 5 },
        fixedWidth: btnWidth,
        // wordWrap pour que les noms longs (Mur de Bouclier, Pluie de Flèches…)
        // ne débordent pas du bouton — ils wrappent sur 2 lignes au pire.
        wordWrap: { width: btnWidth - 12 },
      });
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => this.selectSkill(i));
      ensureTouchTarget(btn);
      // Stocker la position d'origine pour pouvoir y revenir au reset (anti-drift LOT B1)
      (btn as Phaser.GameObjects.Text & { _baseX?: number; _baseY?: number })._baseX = btnX;
      (btn as Phaser.GameObjects.Text & { _baseX?: number; _baseY?: number })._baseY = barY;

      this.skillButtons.push(btn);
    }
  }

  private drawTurnOrder(): void {
    // Frise horizontale au-dessus de la grille
    const y = GRID.ORIGIN_Y - 20;
    this.add.text(GRID.ORIGIN_X, y - 2, 'Tour:', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    });

    this.turnOrderIcons = [];
    const allCombatants: CombatantState[] = [...this.players, ...this.enemies];
    const startX = GRID.ORIGIN_X + 40;
    // Sprint 3 mobile (G2) : Math.max(30, ...) garantit un floor de 30 px
    // si beaucoup de combattants (sécurité). Quand l'espacement est serré
    // (< 50 px = chevauchement avec le label complet), on bascule sur emoji
    // seul + font 9 px pour gagner ~10 px par icône. Desktop (8 colonnes
    // × 64 = 512 - 50 = 462 / 8 = 57 → clamp 55) inchangé.
    const rawSpacing = Math.floor((GRID.COLS * GRID.TILE_SIZE - 50) / allCombatants.length);
    const spacing = Math.max(30, Math.min(55, rawSpacing));
    const tight = spacing < 50;
    const iconFontSize = tight ? '9px' : '11px';
    const iconPadding = tight ? { x: 2, y: 1 } : { x: 3, y: 1 };

    for (let i = 0; i < allCombatants.length; i++) {
      const c = allCombatants[i];
      const x = startX + i * spacing;

      let label: string;
      let fullName: string;
      let color: string;
      if (c.isPlayer) {
        const cls = CLASS_DEFINITIONS[this.getPlayerClass(c)];
        // Nom court : retire articles français ("Le ", "La ", "L'", "El ", "Les ")
        // puis prend les 4 premières lettres du 1er mot significatif.
        // Évite "Le C" tronqué moche pour "Le Capitaine" → "Capi" propre.
        const stripped = c.name.replace(/^(Le |La |L'|Les |El |Don |Dame )/i, '');
        const firstWord = stripped.split(/\s+/)[0];
        // Si l'espacement est serré, on n'affiche que l'emoji (pas le shortName)
        const shortName = firstWord.substring(0, 4);
        label = tight
          ? `${cls?.emoji ?? '🏴‍☠️'}`
          : `${cls?.emoji ?? '🏴‍☠️'} ${shortName}`;
        fullName = c.name;
        color = '#3498db';
      } else {
        const def = ENEMY_DEFINITIONS[c.enemyType!];
        label = def.emoji;
        fullName = c.name;
        color = '#' + def.color.toString(16).padStart(6, '0');
      }

      const icon = this.add.text(x, y - 2, label, {
        fontSize: iconFontSize, color, fontFamily: FONT_BODY, fontStyle: 'bold',
        backgroundColor: '#1a1a2e', padding: iconPadding,
      });
      // Tooltip au survol : affiche le nom complet
      icon.setInteractive({ useHandCursor: false });
      icon.on('pointerover', (pointer: Phaser.Input.Pointer) => {
        this.tooltip.setText(fullName);
        this.tooltip.setPosition(
          Math.min(pointer.x + 12, GAME_W - this.tooltip.width - 4),
          Math.max(pointer.y - 20, 4),
        );
        this.tooltip.setVisible(true);
      });
      icon.on('pointerout', () => this.tooltip.setVisible(false));
      this.turnOrderIcons.push(icon);
    }
  }

  private drawConsumableBar(x: number, y: number, _width: number): void {
    // Sauver la position pour pouvoir redessiner
    this.consumableBarPos = { x, y, w: _width };
    this.redrawConsumableBar();
  }

  private redrawConsumableBar(): void {
    // Supprimer les anciens éléments
    for (const el of this.consumableElements) el.destroy();
    this.consumableElements = [];

    const { x, y, w } = this.consumableBarPos;
    const consumables = this.inventory.getConsumables();
    if (consumables.length === 0) return;

    const _mob = isMobileOrTablet();

    if (_mob) {
      // Mobile : layout horizontal sur une seule ligne, sous la barre de skills
      // (cf. drawUI : drawConsumableBar appelé à gridBottom+64 en mobile, hors
      // du panel info qui déborde). Pas de titre — l'icône violette suffit (G1).
      const maxItems = Math.min(consumables.length, 3);
      const gap = 4;
      const btnW = Math.floor((w - gap * (maxItems - 1)) / maxItems);

      for (let i = 0; i < consumables.length; i++) {
        const c = consumables[i];
        const def = CONSUMABLE_DEFINITIONS[c.definitionId];
        if (!def) continue;
        const bx = x + i * (btnW + gap);

        const btn = this.add.text(bx, y, `${def.emoji} ${def.name}`, {
          fontSize: '10px', color: '#9b59b6', fontFamily: FONT_BODY,
          backgroundColor: '#1a1a2e', padding: { x: 4, y: 4 },
          fixedWidth: btnW,
          align: 'center',
          wordWrap: { width: btnW - 8 },
        });
        btn.setInteractive({ useHandCursor: true });
        btn.on('pointerdown', () => {
          if (this.phase !== 'player_turn' || this.isAnimating) return;
          this.useConsumableInCombat(c.uid);
        });
        ensureTouchTarget(btn);
        this.consumableElements.push(btn);
      }
      return;
    }

    // Desktop : layout vertical dans le panel droit (comportement d'origine)
    const title = this.add.text(x, y, '--- Consommables ---', {
      fontSize: '10px', color: '#9b59b6', fontFamily: FONT_BODY,
    });
    this.consumableElements.push(title);
    let cY = y + 16;

    for (const c of consumables) {
      const def = CONSUMABLE_DEFINITIONS[c.definitionId];
      if (!def) continue;

      const btn = this.add.text(x, cY, `${def.emoji} ${def.name}`, {
        fontSize: '10px', color: '#9b59b6', fontFamily: FONT_BODY,
        backgroundColor: '#1a1a2e', padding: { x: 4, y: 2 },
      });
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        if (this.phase !== 'player_turn' || this.isAnimating) return;
        this.useConsumableInCombat(c.uid);
      });
      if (!isTouchDevice()) {
        // Desktop : hover classique
        btn.on('pointerover', () => {
          btn.setColor('#c39bd3');
          this.tooltip.setText(`${def.emoji} ${def.name}\n${def.description}`);
          this.tooltip.setPosition(
            Math.min(btn.x + btn.width + 10, GAME_W - this.tooltip.width - 4),
            Math.min(btn.y, GAME_H - this.tooltip.height - 4),
          );
          this.tooltip.setVisible(true);
        });
        btn.on('pointerout', () => {
          btn.setColor('#9b59b6');
          this.tooltip.setVisible(false);
        });
      }
      ensureTouchTarget(btn);
      this.consumableElements.push(btn);
      cY += 20;
    }
  }

  private useConsumableInCombat(uid: string): void {
    const consumable = this.inventory.getConsumables().find(c => c.uid === uid);
    if (!consumable) return;
    const def = CONSUMABLE_DEFINITIONS[consumable.definitionId];
    if (!def) return;

    // Si c'est un heal et qu'on a plusieurs persos vivants → sélecteur de cible
    if (def.effect.heal && this.players.filter(p => p.isAlive).length > 1) {
      this.showHealTargetSelector(uid, def.effect.heal);
      return;
    }

    // Sprint 83 — Consommable de dégâts en zone (bombe) : entrer en mode ciblage
    // au lieu d'appliquer aveuglément autour du joueur.
    if (def.effect.damage && def.effect.aoe && def.effect.aoe > 0) {
      this.enterThrowTargetingMode(uid, def.effect.damage, def.effect.aoe, def.effect.throwRange ?? 4);
      return;
    }

    this.applyConsumable(uid, this.player);
  }

  /** Active le mode ciblage pour un consommable de zone. Le joueur clique une case → AoE. */
  private enterThrowTargetingMode(uid: string, damage: number, aoeRadius: number, throwRange: number): void {
    this.exitThrowTargetingMode(); // au cas où

    // Calculer les cases dans la portée de lancer (Manhattan distance)
    const throwTiles: GridPosition[] = [];
    const pCol = this.player.position.col;
    const pRow = this.player.position.row;
    for (let row = 0; row < GRID.ROWS; row++) {
      for (let col = 0; col < GRID.COLS; col++) {
        const dist = Math.abs(col - pCol) + Math.abs(row - pRow);
        if (dist > 0 && dist <= throwRange) throwTiles.push({ col, row });
      }
    }

    this.pendingThrow = { uid, throwRange, aoeRadius, damage, throwTiles };

    // Surligner les cases éligibles en orange
    for (const t of throwTiles) {
      this.tiles[t.row][t.col].setFillStyle(0xe67e22, 0.4);
    }

    // Bandeau "🎯 Cliquez sur une case (rayon X)" + bouton Annuler
    const banner = this.add.text(GAME_CX, 12, `🎯 Choisir la cible de la bombe (rayon ${aoeRadius}, portée ${throwRange})`, {
      fontSize: '12px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
      backgroundColor: '#2c1a0a', padding: { x: 10, y: 5 },
    }).setOrigin(0.5, 0).setDepth(150);
    this.throwTargetingElements.push(banner);

    const cancelBtn = this.add.text(GAME_CX, 40, '[ Annuler ]', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 8, y: 4 },
    }).setOrigin(0.5, 0).setDepth(150);
    cancelBtn.setInteractive({ useHandCursor: true });
    cancelBtn.on('pointerdown', () => this.exitThrowTargetingMode());
    this.throwTargetingElements.push(cancelBtn);
  }

  /** Quitte le mode ciblage et nettoie les surlignages. */
  private exitThrowTargetingMode(): void {
    this.pendingThrow = null;
    for (const e of this.throwTargetingElements) e.destroy();
    this.throwTargetingElements = [];
    this.calculateReachableTiles();
    this.highlightTiles();
  }

  /**
   * Applique l'AoE d'un consommable de zone centrée sur une case cible.
   * Appelé depuis onTileClick quand pendingThrow est actif.
   */
  private applyThrowAt(target: GridPosition): void {
    const pending = this.pendingThrow;
    if (!pending) return;
    // Vérifier que la cible est dans la portée de lancer
    if (!pending.throwTiles.some(t => t.col === target.col && t.row === target.row)) return;

    // Consommer l'item APRÈS validation de la cible (pas avant — évite la perte si annulation)
    const consumed = this.inventory.useConsumable(pending.uid);
    if (!consumed) {
      this.exitThrowTargetingMode();
      return;
    }

    this.exitThrowTargetingMode();
    this.sfx.cannon();

    // Animation de projectile depuis le joueur vers la cible, puis explosion
    this.playProjectile(this.player.position, target, () => {
      // AoE : tous les ennemis dans le rayon autour de la case cible
      let hit = 0;
      for (const enemy of this.enemies) {
        if (!enemy.isAlive) continue;
        const dist = this.gridDistance(target, enemy.position);
        if (dist <= pending.aoeRadius) {
          enemy.stats.hp = Math.max(0, enemy.stats.hp - pending.damage);
          const ePix = this.gridToPixel(enemy.position);
          fxExplosion(this, ePix.x, ePix.y);
          this.showDamageNumber(enemy.position, pending.damage);
          if (enemy.stats.hp <= 0) {
            enemy.isAlive = false;
            this.sfx.death();
          }
          hit++;
        }
      }
      // Explosion centrale même si rien touché (feedback visuel)
      const centerPix = this.gridToPixel(target);
      fxExplosion(this, centerPix.x, centerPix.y);
      this.screenShake(6);

      if (hit > 0) this.setLog(`💣 Boom ! ${pending.damage} dégâts sur ${hit} ennemi(s) !`);
      else this.setLog(`💣 La bombe explose, mais ne touche personne...`);

      // Vérif fin de combat
      if (this.allEnemiesDead()) {
        this.gameOver(true);
        return;
      }
      this.calculateReachableTiles();
      this.highlightTiles();
      this.refreshUI();
      this.redrawConsumableBar();
    });
  }

  /** Affiche un sélecteur pour choisir quel allié soigner */
  private showHealTargetSelector(uid: string, healAmount: number): void {
    // Overlay semi-transparent
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.5).setDepth(150).setInteractive();
    const title = this.add.text(GAME_CX, GAME_CY - 100, `🍺 Soigner qui ? (+${healAmount} HP)`, {
      fontSize: '14px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(151);

    const elements: Phaser.GameObjects.GameObject[] = [overlay, title];
    const alivePlayers = this.players.filter(p => p.isAlive);

    alivePlayers.forEach((p, i) => {
      const cls = CLASS_DEFINITIONS[this.getPlayerClass(p)];
      const btnY = (GAME_CY - 60) + i * 45;
      const bg = this.add.rectangle(GAME_CX, btnY, 300, 36, 0x1a2a40).setStrokeStyle(1, 0x2a3a5c).setDepth(151);
      const txt = this.add.text(GAME_CX, btnY, `${cls.emoji} ${p.name} (${p.stats.hp}/${p.stats.maxHp} HP)`, {
        fontSize: '12px', color: '#ecf0f1', fontFamily: FONT_BODY,
      }).setOrigin(0.5).setDepth(152);
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerdown', () => {
        elements.forEach(e => e.destroy());
        this.applyConsumable(uid, p);
      });
      bg.on('pointerover', () => bg.setStrokeStyle(2, 0xf1c40f));
      bg.on('pointerout', () => bg.setStrokeStyle(1, 0x2a3a5c));
      elements.push(bg, txt);
    });

    // Bouton annuler
    const cancelBtn = this.add.text(GAME_CX, (GAME_CY - 60) + alivePlayers.length * 45 + 10, '[ Annuler ]', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 8, y: 4 },
    }).setOrigin(0.5).setDepth(151);
    cancelBtn.setInteractive({ useHandCursor: true });
    cancelBtn.on('pointerdown', () => elements.forEach(e => e.destroy()));
    elements.push(cancelBtn);
  }

  /** Applique un consommable sur un perso cible */
  private applyConsumable(uid: string, target: CombatantState): void {
    const effect = this.inventory.useConsumable(uid);
    if (!effect) return;

    this.sfx.select();

    if (effect.heal) {
      const before = target.stats.hp;
      target.stats.hp = Math.min(target.stats.hp + effect.heal, target.stats.maxHp);
      const actualHeal = target.stats.hp - before;
      this.setLog(`${target.name} se soigne de ${actualHeal} HP !`);
      const targetSprite = this.playerSprites.get(target.id);
      if (targetSprite && actualHeal > 0) this.playHealEffect(targetSprite, target.position, actualHeal, target);
    }
    if (effect.bonusPa) {
      target.currentPa += effect.bonusPa;
      this.setLog(`${target.name} gagne +${effect.bonusPa} PA !`);
    }
    if (effect.bonusPm) {
      target.currentPm += effect.bonusPm;
      this.setLog(`${target.name} gagne +${effect.bonusPm} PM !`);
    }
    // NOTE : l'effet damage+aoe est géré par enterThrowTargetingMode + applyThrowAt
    // (Sprint 83). Ici, applyConsumable est uniquement appelé pour heal/bonus.
    // Si quelqu'un appelle applyConsumable avec un consommable damage sans passer par
    // useConsumableInCombat, on ignore — le mode ciblage est obligatoire pour les bombes.

    // Rafraîchir l'UI et la barre de consommables
    this.calculateReachableTiles();
    this.highlightTiles();
    this.refreshUI();
    this.redrawConsumableBar();

    // Vérifier victoire après dégâts de zone
    if (this.enemies.every(e => !e.isAlive)) {
      this.gameOver(true);
    }
  }

  private refreshTurnOrder(): void {
    const allCombatants: CombatantState[] = [...this.players, ...this.enemies];

    for (let i = 0; i < allCombatants.length; i++) {
      const c = allCombatants[i];
      const icon = this.turnOrderIcons[i];
      if (!icon) continue;

      if (!c.isAlive) {
        icon.setAlpha(0.2);
        icon.setBackgroundColor('#111111');
        continue;
      }

      // Surligner celui qui joue
      const isActive = (this.phase === 'player_turn' && c.isPlayer && c.id === this.player.id) ||
        (this.phase === 'enemy_turn' && !c.isPlayer &&
          this.currentEnemyIndex < this.enemies.length &&
          this.enemies[this.currentEnemyIndex].id === c.id);

      if (isActive) {
        icon.setBackgroundColor('#3a536b');
      } else {
        icon.setBackgroundColor('#1a1a2e');
      }
    }
  }

  private selectSkill(index: number): void {
    if (this.phase !== 'player_turn' || this.isAnimating) return;
    if (index < 0 || index >= this.activeSkills.length) return;

    this.selectedSkill = this.activeSkills[index];

    // Mise à jour visuelle des boutons
    this.updateSkillButtons(index);

    this.sfx.select();

    // Recalculer les cases attaquables avec la nouvelle portée
    this.calculateReachableTiles();
    this.highlightTiles();

    // Bug 1 — sans ce refresh, le panneau "Skill actif" à droite et le bandeau
    // (portée, dégâts estimés) restaient figés sur le skill précédent jusqu'au
    // prochain tour. refreshUI() resynchronise tous les éléments dépendants
    // de selectedSkill (skillInfoText, statsText, boutons).
    if (this.skillInfoText) this.refreshUI();
  }

  /**
   * Met à jour `activeSkills` selon la classe du joueur actif courant
   * (3 skills de base + skill de classe si disponible). Préserve la sélection
   * courante si elle existe toujours dans la nouvelle liste, sinon réinitialise
   * sur le 1er skill.
   */
  private refreshActiveSkillsForCurrentPlayer(): void {
    const cls = this.getPlayerClass(this.player);
    this.activeSkills = getActiveSkillsForClass(cls);
    if (!this.activeSkills.includes(this.selectedSkill)) {
      this.selectedSkill = this.activeSkills[0];
    }
  }

  /** Détruit et reconstruit la barre de skills (utilisé si le nombre de skills change). */
  private rebuildSkillBar(): void {
    for (const btn of this.skillButtons) btn.destroy();
    this.skillButtons = [];
    this.drawSkillBar();
  }

  private refreshSkillButtons(): void {
    // Rechercher dans activeSkills (base + classe) et non dans SKILLS (base uniquement),
    // sinon les skills de classe (Médecin, Tireur, Guerrier) retournent -1 et l'UI se désynchronise.
    const currentIndex = this.activeSkills.indexOf(this.selectedSkill);
    this.updateSkillButtons(currentIndex);
  }

  /** Met à jour le texte et la couleur de tous les boutons de skills */
  private updateSkillButtons(selectedIndex: number): void {
    for (let i = 0; i < this.skillButtons.length; i++) {
      const skill = this.activeSkills[i];
      if (!skill) continue;
      const isSelected = i === selectedIndex;
      const canAfford = this.player.currentPa >= skill.paCost;
      // Pas de préfixe ⚠ — la couleur rouge + le shake suffisent à signaler.
      const paLabel = `${skill.paCost}PA`;

      this.skillButtons[i].setText(`${skill.emoji}${skill.name} ${paLabel}`);

      if (isSelected) {
        this.skillButtons[i].setColor('#f1c40f');
        this.skillButtons[i].setBackgroundColor('#3a536b');
      } else if (canAfford) {
        this.skillButtons[i].setColor('#bdc3c7');
        this.skillButtons[i].setBackgroundColor('#2c3e50');
      } else {
        this.skillButtons[i].setColor('#e74c3c');
        this.skillButtons[i].setBackgroundColor('#1a1a2e');
      }
    }
  }

  private refreshUI(): void {
    const turnLabel = this.phase === 'player_turn' ? '🟢 Ton tour' : '🔴 Tour Ennemis';
    this.turnText.setText(turnLabel);

    // HP et stats tous les joueurs + highlight actif
    for (const p of this.players) {
      const bar = this.playerHpBars.get(p.id);
      if (bar) {
        const ratio = p.stats.hp / p.stats.maxHp;
        bar.setScale(p.isAlive ? ratio : 0, 1);
        bar.setFillStyle(ratio > 0.5 ? COLORS.HP_GREEN : COLORS.HP_RED);
      }
      // Mettre à jour les stats individuelles
      const pStat = this.children.getByName(`player_stat_${p.id}`) as Phaser.GameObjects.Text;
      if (pStat) {
        if (p.isAlive) {
          pStat.setText(`HP:${p.stats.hp}/${p.stats.maxHp} PA:${p.currentPa} PM:${p.currentPm}`);
        } else {
          pStat.setText('MORT');
          pStat.setColor('#7f8c8d');
        }
      }
      // Highlight du cadre
      const pBg = this.children.getByName(`player_bg_${p.id}`) as Phaser.GameObjects.Rectangle;
      if (pBg) {
        pBg.setStrokeStyle(p.id === this.player.id ? 2 : 0, 0xf1c40f);
      }
    }

    this.statsText.setText(
      `HP: ${this.player.stats.hp}/${this.player.stats.maxHp}  ` +
      `PA: ${this.player.currentPa}/${this.player.stats.pa}  ` +
      `PM: ${this.player.currentPm}/${this.player.stats.pm}\n` +
      `ATK: ${this.player.stats.attack}  DEF: ${this.player.stats.defense}  ` +
      `Portée: ${this.selectedSkill.range + this.getPlayerBonusRange()}`
    );

    // HP + stats ennemis
    for (const enemy of this.enemies) {
      const bar = this.enemyHpBars.get(enemy.id);
      if (bar) {
        const ratio = enemy.stats.hp / enemy.stats.maxHp;
        bar.setScale(ratio, 1);
        bar.setFillStyle(ratio > 0.5 ? COLORS.HP_GREEN : COLORS.HP_RED);
        // LOT B3 : masquer complètement le panel HP+stats des ennemis morts
        // (avant : alpha 0.3 + texte "MORT" → restait visible à travers la
        // popup Victoire qui n'a qu'un overlay 60% opaque).
        if (!enemy.isAlive) bar.setVisible(false);
      }

      const statText = this.enemyStatTexts.get(enemy.id);
      if (statText) {
        if (enemy.isAlive) {
          statText.setText(
            `HP:${enemy.stats.hp}/${enemy.stats.maxHp}  ATK:${enemy.stats.attack}  DEF:${enemy.stats.defense}  PM:${enemy.stats.pm}`
          );
        } else {
          statText.setVisible(false);
        }
      }

      // Aussi masquer le tag de nom de l'ennemi mort pour ne pas laisser
      // de "fantôme" visible à travers la popup Victoire
      if (!enemy.isAlive) {
        const nameTag = this.children.getByName(`enemy_name_${enemy.id}`) as Phaser.GameObjects.Text | null;
        if (nameTag) nameTag.setVisible(false);
      }
    }

    // Indicateurs visuels : debuffs + modificateurs d'élite sur les sprites
    const DEBUFF_ICONS: Record<string, string> = {
      immobilized: '⛓',
      cursed_aura: '🌊',
      shield_wall: '🛡',
    };
    for (const [id, indicator] of this.debuffIndicators) {
      const combatant = [...this.players, ...this.enemies].find(c => c.id === id);
      if (!combatant || !combatant.isAlive) {
        indicator.setText('');
        continue;
      }
      const pos = this.gridToPixel(combatant.position);
      indicator.setPosition(pos.x, pos.y + GRID.TILE_SIZE / 2 - 2);

      // Préfixe modificateur d'élite (persistant, contrairement aux debuffs)
      const modifierEmoji = combatant.eliteModifier
        ? ELITE_MODIFIERS[combatant.eliteModifier].emoji
        : '';

      const debuffSet = this.debuffs.get(id);
      const debuffIcons = debuffSet
        ? [...debuffSet].map(d => {
            if (d.startsWith('taunted_by_')) return '🗣';
            return DEBUFF_ICONS[d] ?? '';
          }).filter(Boolean).join('')
        : '';

      indicator.setText(modifierEmoji + debuffIcons);
    }

    // Infos du skill sélectionné
    const totalRange = this.selectedSkill.range + this.getPlayerBonusRange();
    const rawDmg = Math.max(1, this.player.stats.attack);
    const skillDmg = Math.max(1, Math.round(rawDmg * this.selectedSkill.damage));
    const minRange = this.selectedSkill.minRange ?? 1;
    const rangeLabel = minRange > 1 ? `${minRange}-${totalRange}` : `${totalRange}`;
    this.skillInfoText.setText(
      `${this.selectedSkill.emoji} ${this.selectedSkill.name}\n` +
      `${this.selectedSkill.description}\n` +
      `Dégâts: ~${skillDmg}  PA: ${this.selectedSkill.paCost}  Portée: ${rangeLabel}` +
      (this.selectedSkill.knockback > 0 ? `  Recul: ${this.selectedSkill.knockback}` : '')
    );

    // Mettre à jour le bandeau joueur actif
    this.refreshBanner();

    this.refreshSkillButtons();
    this.refreshTurnOrder();
  }

  private refreshBanner(): void {
    const p = this.player;
    const cls = CLASS_DEFINITIONS[this.getPlayerClass(p)];

    this.bannerNameText.setText(`${cls.emoji} ${p.name}`);

    const hpRatio = p.stats.hp / p.stats.maxHp;
    this.bannerHpBar.setScale(p.isAlive ? hpRatio : 0, 1);
    this.bannerHpBar.setFillStyle(hpRatio > 0.5 ? COLORS.HP_GREEN : COLORS.HP_RED);
    this.bannerHpText.setText(`${p.stats.hp}/${p.stats.maxHp}`);

    this.bannerPaText.setText(`⚡${p.currentPa}PA`);
    this.bannerPmText.setText(`👟${p.currentPm}PM`);
  }

  private setLog(message: string): void {
    this.logText.setText(`> ${message}`);
    // Ajouter à l'historique (garder les 6 dernières)
    this.combatLogs.push(message);
    if (this.combatLogs.length > 6) this.combatLogs.shift();
    if (this.combatLogText) {
      this.combatLogText.setText(this.combatLogs.map(l => `· ${l}`).join('\n'));
    }
  }

  // ===========================
  // LOGIQUE DE TOUR
  // ===========================

  /** Démarre le tour du prochain joueur vivant, ou passe aux ennemis */
  private startNextPlayerTurn(): void {
    // Trouver le prochain joueur vivant
    while (this.activePlayerIndex < this.players.length && !this.players[this.activePlayerIndex].isAlive) {
      this.activePlayerIndex++;
    }

    if (this.activePlayerIndex >= this.players.length) {
      // Tous les joueurs ont joué → tour des ennemis
      this.startEnemyPhase();
      return;
    }

    // Activer ce joueur
    this.player = this.players[this.activePlayerIndex];
    this.playerSprite = this.playerSprites.get(this.player.id)!;
    this.playerHpBar = this.playerHpBars.get(this.player.id)!;

    this.phase = 'player_turn';
    this.player.currentPa = this.player.stats.pa;
    this.player.currentPm = this.player.stats.pm;

    // Mettre à jour les skills actifs selon la classe du joueur courant
    // (chaque membre de l'équipe peut avoir un skill de classe différent).
    const previousLength = this.activeSkills.length;
    this.refreshActiveSkillsForCurrentPlayer();
    this.selectedSkill = this.activeSkills[0];
    // Reconstruire la barre si le nombre de skills a changé (ex : classe sans
    // skill de classe → classe avec skill de classe). Sans ça, certains
    // boutons resteraient orphelins ou manquants.
    if (this.skillButtons.length > 0 && this.activeSkills.length !== previousLength) {
      this.rebuildSkillBar();
    }

    // Nettoyer aura maudit (recalculée à chaque tour ennemi)
    this.removeDebuff(this.player.id, 'cursed_aura');

    // Appliquer debuff immobilisation (Brute) : -1 PM ce tour
    if (this.hasDebuff(this.player.id, 'immobilized')) {
      this.player.currentPm = Math.max(0, this.player.currentPm - 1);
      this.removeDebuff(this.player.id, 'immobilized');
      this.setLog(`⛓️ ${this.player.name} est ralenti (Immobilisation) ! -1 PM`);
    }

    // Passif Médecin : regen 1 HP par tour
    const playerClass = this.getPlayerClass(this.player);
    if (playerClass === 'medic' && this.player.stats.hp < this.player.stats.maxHp) {
      this.player.stats.hp = Math.min(this.player.stats.hp + 1, this.player.stats.maxHp);
    }

    // Highlight le sprite actif
    for (const [id, sprite] of this.playerSprites) {
      const p = this.players.find(pl => pl.id === id);
      if (!p || !p.isAlive) continue;
      const cls = CLASS_DEFINITIONS[this.getPlayerClass(p)];
      if (sprite instanceof Phaser.GameObjects.Rectangle) {
        if (id === this.player.id) {
          sprite.setStrokeStyle(3, 0xf1c40f); // bordure dorée = actif
        } else {
          sprite.setStrokeStyle(2, cls?.outline ?? COLORS.PLAYER_OUTLINE);
        }
      } else {
        // Pour les sprites SVG, on utilise le tint pour indiquer le joueur actif
        sprite.setTint(id === this.player.id ? 0xffff88 : 0xffffff);
      }
    }

    this.calculateReachableTiles();
    this.highlightTiles();
    this.refreshUI();
    const multi = this.players.filter(p => p.isAlive).length > 1;
    const nameLabel = multi ? `Tour de ${this.player.name}` : 'Ton tour';
    this.setLog(`${nameLabel} ! Choisis un skill (1-2-3) puis clique sur une cible.`);

    // Animation de passage de tour (multi-perso uniquement)
    if (multi) {
      const activeCls = CLASS_DEFINITIONS[this.getPlayerClass(this.player)];
      const banner = this.add.text(GAME_CX, GAME_CY - 40, `${activeCls?.emoji ?? '⚔️'} ${this.player.name}`, {
        fontSize: '22px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
        backgroundColor: '#000000', padding: { x: 20, y: 10 },
        stroke: '#000000', strokeThickness: 4,
      }).setOrigin(0.5).setDepth(50).setAlpha(0);
      this.tweens.add({
        targets: banner,
        alpha: { from: 0, to: 1 },
        scaleX: { from: 0.6, to: 1 },
        scaleY: { from: 0.6, to: 1 },
        duration: 200,
        ease: 'Back.Out',
        onComplete: () => {
          this.time.delayedCall(600, () => {
            this.tweens.add({
              targets: banner, alpha: 0, y: GAME_CY - 80,
              duration: 300, ease: 'Power2',
              onComplete: () => banner.destroy(),
            });
          });
        },
      });
    }
  }

  // Rétro-compat : appelée par create()
  private startPlayerTurn(): void {
    this.activePlayerIndex = 0;
    this.resetSkillButtonPositions();
    this.startNextPlayerTurn();
  }

  private endPlayerTurn(): void {
    this.clearHighlights();
    // Nettoyer le Mur de Bouclier (+3 DEF temporaire)
    if (this.hasDebuff(this.player.id, 'shield_wall')) {
      this.player.stats.defense -= 3;
      this.removeDebuff(this.player.id, 'shield_wall');
    }
    // Passer au joueur suivant
    this.activePlayerIndex++;
    this.startNextPlayerTurn();
  }

  private startEnemyPhase(): void {
    this.clearHighlights();
    this.setLog('Tour des ennemis...');
    this.phase = 'enemy_turn';
    this.refreshUI();
    this.currentEnemyIndex = 0;

    this.time.delayedCall(400, () => {
      this.processNextEnemy();
    });
  }

  private processNextEnemy(): void {
    while (this.currentEnemyIndex < this.enemies.length && !this.enemies[this.currentEnemyIndex].isAlive) {
      this.currentEnemyIndex++;
    }

    if (this.currentEnemyIndex >= this.enemies.length) {
      // Tous les ennemis ont joué → retour joueurs
      this.startPlayerTurn();
      return;
    }

    const enemy = this.enemies[this.currentEnemyIndex];
    this.enemyTurn(enemy, () => {
      if (this.allPlayersDead()) {
        this.gameOver(false);
        return;
      }
      this.currentEnemyIndex++;
      this.time.delayedCall(300, () => {
        this.processNextEnemy();
      });
    });
  }

  /** Trouve le joueur vivant le plus proche */
  private findClosestPlayer(from: GridPosition): CombatantState {
    let closest = this.players.find(p => p.isAlive)!;
    let minDist = this.gridDistance(from, closest.position);
    for (const p of this.players) {
      if (!p.isAlive) continue;
      const d = this.gridDistance(from, p.position);
      // IA : focus cibles faibles (HP les plus bas à distance égale)
      if (d < minDist || (d === minDist && p.stats.hp < closest.stats.hp)) {
        minDist = d;
        closest = p;
      }
    }
    return closest;
  }

  private enemyTurn(enemy: CombatantState, onComplete: () => void): void {
    enemy.currentPa = enemy.stats.pa;
    enemy.currentPm = enemy.stats.pm;

    // === CAPACITÉS SPÉCIALES AU DÉBUT DU TOUR ===

    // Maudit : regen 2 HP par tour
    if (enemy.enemyType === 'cursed' && enemy.stats.hp < enemy.stats.maxHp) {
      const heal = Math.min(2, enemy.stats.maxHp - enemy.stats.hp);
      enemy.stats.hp += heal;
      this.setLog(`🌊 ${enemy.name} se régénère (+${heal} HP)`);
    }

    // Modificateur d'élite — Régénération : +2 HP par tour
    const regenHeal = calculateRegenHeal(enemy.stats.hp, enemy.stats.maxHp, enemy.eliteModifier);
    if (regenHeal > 0) {
      enemy.stats.hp += regenHeal;
      this.setLog(`💚 ${enemy.name} (Régén) récupère ${regenHeal} HP`);
    }

    // Sirène : soigne l'allié le plus blessé à portée 4
    if (enemy.enemyType === 'siren') {
      const wounded = this.enemies
        .filter(e => e.isAlive && e.id !== enemy.id && e.stats.hp < e.stats.maxHp
          && this.gridDistance(enemy.position, e.position) <= 4)
        .sort((a, b) => (a.stats.hp / a.stats.maxHp) - (b.stats.hp / b.stats.maxHp));
      if (wounded.length > 0) {
        const healTarget = wounded[0];
        const healAmt = Math.min(3, healTarget.stats.maxHp - healTarget.stats.hp);
        healTarget.stats.hp += healAmt;
        this.setLog(`🧜 ${enemy.name} soigne ${healTarget.name} (+${healAmt} HP)`);
      }
    }

    // Maudit : aura -1 ATK sur les joueurs à portée 2
    if (enemy.enemyType === 'cursed') {
      for (const p of this.players) {
        if (p.isAlive && this.gridDistance(enemy.position, p.position) <= 2) {
          if (!this.hasDebuff(p.id, 'cursed_aura')) {
            this.addDebuff(p.id, 'cursed_aura');
          }
        }
      }
    }

    const def = ENEMY_DEFINITIONS[enemy.enemyType!];
    const range = def?.range ?? 1;
    const target = this.findClosestPlayer(enemy.position);
    const distToTarget = this.gridDistance(enemy.position, target.position);

    const sprite = this.enemySprites.get(enemy.id)!;

    // Les ennemis à distance ne bougent pas s'ils sont déjà à portée et ont la LOS
    let path: GridPosition[] = [];
    if (distToTarget > range || (range > 1 && !this.hasLineOfSight(enemy.position, target.position, target.id))) {
      // Rôdeur spectral : traverse les ennemis (ignore collision)
      const ghostMove = enemy.enemyType === 'fast';
      path = this.findPathToward(enemy.position, target.position, enemy.currentPm, ghostMove);
      // Pour les ennemis distance, s'arrêter dès qu'on est à portée avec LOS
      if (range > 1 && path.length > 0) {
        for (let i = 0; i < path.length; i++) {
          const d = this.gridDistance(path[i], target.position);
          if (d <= range && this.hasLineOfSight(path[i], target.position, target.id)) {
            path = path.slice(0, i + 1);
            break;
          }
        }
      }
    }

    if (path.length > 0) {
      const destination = path[path.length - 1];
      const oldEnemyPos = { ...enemy.position };
      this.isAnimating = true;

      this.moveEntity(sprite, enemy, destination, () => {
        enemy.currentPm -= path.length;
        // AoO : les joueurs adjacents à l'ancienne position frappent l'ennemi
        this.checkAndApplyAoO(enemy, oldEnemyPos, destination, () => {
          if (!enemy.isAlive) {
            this.isAnimating = false;
            this.refreshUI();
            onComplete();
            return;
          }
          this.tryEnemyAttacks(enemy, () => {
            this.isAnimating = false;
            this.refreshUI();
            onComplete();
          });
        });
      });
    } else {
      this.tryEnemyAttacks(enemy, () => {
        this.refreshUI();
        onComplete();
      });
    }
  }

  // Enchaîne les attaques ennemies contre le joueur le plus proche à portée
  private tryEnemyAttacks(enemy: CombatantState, onComplete: () => void): void {
    if (enemy.currentPa < 2 || this.allPlayersDead()) {
      onComplete();
      return;
    }

    const def = ENEMY_DEFINITIONS[enemy.enemyType!];
    const range = def?.range ?? 1;

    // Trouver un joueur à portée
    let target: CombatantState | null = null;
    let minDist = Infinity;
    for (const p of this.players) {
      if (!p.isAlive) continue;
      const d = this.gridDistance(enemy.position, p.position);
      if (d <= range && d < minDist) {
        if (range > 1 && !this.hasLineOfSight(enemy.position, p.position, p.id)) continue;
        minDist = d;
        target = p;
      }
    }

    if (!target) {
      onComplete();
      return;
    }

    enemy.currentPa -= 2;
    this.performAttack(enemy, target, null, () => {
      this.time.delayedCall(150, () => {
        this.tryEnemyAttacks(enemy, onComplete);
      });
    });
  }

  // ===========================
  // ACTIONS JOUEUR
  // ===========================

  private onTileClick(col: number, row: number): void {
    if (this.phase !== 'player_turn' || this.isAnimating) return;

    // === MODE CIBLAGE BOMBE (Sprint 83) ===
    // Si une bombe attend une cible, le clic la lance ici.
    if (this.pendingThrow) {
      this.applyThrowAt({ col, row });
      return;
    }

    // === SKILLS SPÉCIAUX (ciblage allié ou self) ===
    if (this.selectedSkill.special === 'shield' && col === this.player.position.col && row === this.player.position.row) {
      // Mur de Bouclier : +3 DEF ce tour (self-cast)
      if (this.player.currentPa < this.selectedSkill.paCost) { this.showInsufficientPaFeedback(); return; }
      this.player.currentPa -= this.selectedSkill.paCost;
      this.player.stats.defense += 3;
      this.addDebuff(this.player.id, 'shield_wall'); // retiré en fin de tour
      this.setLog(`🛡️ ${this.player.name} lève un Mur de Bouclier ! +3 DEF`);
      this.sfx.select();
      this.calculateReachableTiles(); this.highlightTiles(); this.refreshUI();
      return;
    }

    if (this.selectedSkill.targetAlly) {
      // Skill ciblant un allié (soin, purification)
      // Si la case cliquée contient un allié à portée → exécuter le skill.
      // Sinon → ne pas return : laisser le code continuer pour autoriser le déplacement.
      const ally = this.players.find(p => p.isAlive && p.position.col === col && p.position.row === row);
      if (ally) {
        const dist = this.gridDistance(this.player.position, { col, row });
        if (dist > this.selectedSkill.range) return;
        if (this.player.currentPa < this.selectedSkill.paCost) { this.showInsufficientPaFeedback(); return; }
        this.player.currentPa -= this.selectedSkill.paCost;

        if (this.selectedSkill.special === 'heal_ally' && this.selectedSkill.healAmount) {
          const healAmt = Math.min(this.selectedSkill.healAmount, ally.stats.maxHp - ally.stats.hp);
          ally.stats.hp += healAmt;
          this.setLog(`💚 ${this.player.name} soigne ${ally.name} (+${healAmt} HP)`);
          const allySprite = this.playerSprites.get(ally.id);
          if (allySprite) this.playHealEffect(allySprite, ally.position, healAmt, ally);
        }
        if (this.selectedSkill.special === 'purify') {
          this.removeDebuff(ally.id, 'immobilized');
          this.removeDebuff(ally.id, 'cursed_aura');
          this.setLog(`✨ ${this.player.name} purifie ${ally.name} !`);
        }
        this.sfx.select();
        this.calculateReachableTiles(); this.highlightTiles(); this.refreshUI();
        return;
      }
      // Pas d'allié cliqué → fall-through vers la logique de déplacement ci-dessous
    }

    if (this.selectedSkill.special === 'taunt' && this.isInList(this.attackableTiles, col, row)) {
      const target = this.getEnemyAt(col, row);
      if (!target) return;
      if (this.player.currentPa < this.selectedSkill.paCost) { this.showInsufficientPaFeedback(); return; }
      this.player.currentPa -= this.selectedSkill.paCost;
      this.addDebuff(target.id, `taunted_by_${this.player.id}`);
      this.setLog(`🗣️ ${this.player.name} provoque ${target.name} !`);
      this.sfx.select();
      this.calculateReachableTiles(); this.highlightTiles(); this.refreshUI();
      return;
    }

    // Attaque avec le skill sélectionné ?
    if (this.isInList(this.attackableTiles, col, row)) {
      const target = this.getEnemyAt(col, row);
      if (!target) return;

      this.isAnimating = true;
      this.player.currentPa -= this.selectedSkill.paCost;

      this.performAttack(this.player, target, this.selectedSkill, () => {
        this.isAnimating = false;

        if (this.allEnemiesDead()) {
          this.gameOver(true);
          return;
        }

        this.calculateReachableTiles();
        this.highlightTiles();
        this.refreshUI();
      });
      return;
    }

    // Clic sur un ennemi vivant hors de portée attaquable → feedback PA si c'est la cause
    if (!this.selectedSkill.targetAlly && this.selectedSkill.special !== 'shield') {
      const clickedEnemy = this.getEnemyAt(col, row);
      if (clickedEnemy && clickedEnemy.isAlive && this.player.currentPa < this.selectedSkill.paCost) {
        this.showInsufficientPaFeedback();
        return;
      }
    }

    // Déplacement ?
    if (this.isInList(this.movableTiles, col, row)) {
      const distance = this.gridDistance(this.player.position, { col, row });
      if (distance > this.player.currentPm) return;

      // Crabe : Étreinte — impossible de s'éloigner d'un crabe adjacent
      const grapplingCrab = this.enemies.find(e =>
        e.isAlive && e.enemyType === 'crab'
        && this.gridDistance(this.player.position, e.position) === 1
        && this.gridDistance({ col, row }, e.position) > 1
      );
      if (grapplingCrab) {
        this.setLog(`🦀 Étreinte ! Impossible de fuir ${grapplingCrab.name} !`);
        return;
      }

      const oldPos = { ...this.player.position };
      this.isAnimating = true;
      this.clearHighlights();

      this.sfx.move();
      this.moveEntity(this.playerSprite, this.player, { col, row }, () => {
        this.player.currentPm -= distance;

        // AoO : les ennemis adjacents à l'ancienne position frappent
        this.checkAndApplyAoO(this.player, oldPos, { col, row }, () => {
          this.isAnimating = false;
          // Vérifier défaite après AoO
          if (this.allPlayersDead()) { this.gameOver(false); return; }
          this.calculateReachableTiles();
          this.highlightTiles();
          this.refreshUI();
          this.setLog(`Déplacement (${this.player.currentPm} PM restants)`);
        });
      });
    }
  }

  // ===========================
  // COMBAT
  // ===========================

  // skill = null pour les attaques ennemies (dégâts normaux)
  private performAttack(attacker: CombatantState, defender: CombatantState, skill: SkillDefinition | null, onComplete: () => void): void {
    const attackerSprite = attacker.isPlayer
      ? this.playerSprites.get(attacker.id)!
      : this.enemySprites.get(attacker.id)!;
    const targetSprite = defender.isPlayer
      ? this.playerSprites.get(defender.id)!
      : this.enemySprites.get(defender.id)!;

    const applyDamage = () => {
      const multiplier = skill ? skill.damage : 1;
      // Passif Guerrier : +1 DEF par ennemi adjacent quand le joueur se fait attaquer
      let defenseBonus = 0;
      if (defender.isPlayer && this.run.getSelectedClass() === 'warrior') {
        defenseBonus = this.enemies.filter(e => e.isAlive && this.gridDistance(e.position, defender.position) === 1).length;
      }
      // Passif Zombie meute : +1 ATK par zombie adjacent à la même cible
      let attackBonus = 0;
      if (!attacker.isPlayer && attacker.enemyType === 'zombie') {
        attackBonus = this.enemies.filter(e =>
          e.isAlive && e.id !== attacker.id && e.enemyType === 'zombie'
          && this.gridDistance(e.position, defender.position) === 1
        ).length;
      }
      // Passif Requin : +2 ATK contre cibles <50% HP
      if (!attacker.isPlayer && attacker.enemyType === 'shark' && defender.stats.hp < defender.stats.maxHp * 0.5) {
        attackBonus += 2;
      }

      // Aura Maudit : -1 ATK si le joueur est affecté
      let attackMalus = 0;
      if (attacker.isPlayer && this.hasDebuff(attacker.id, 'cursed_aura')) {
        attackMalus = 1;
      }

      // Modificateur d'élite — Enragé : +2 ATK quand l'attaquant a < 30% HP
      attackBonus += applyEnragedBonus(attacker.stats.hp, attacker.stats.maxHp, attacker.eliteModifier);

      const rawDamage = Math.max(1, (attacker.stats.attack + attackBonus - attackMalus) - (defender.stats.defense + defenseBonus));
      let damage = Math.max(1, Math.round(rawDamage * multiplier));

      // Modificateurs d'élite défenseur : Bouclier phase (-50% HP>50%) + Résistant (-1 min 1)
      damage = applyDefenderModifiers(damage, defender.stats.hp, defender.stats.maxHp, defender.eliteModifier);

      defender.stats.hp = Math.max(0, defender.stats.hp - damage);

      // Modificateur d'élite — Épines : le défenseur renvoie 1 dégât à l'attaquant en mêlée
      const thornDmg = calculateThornsDamage(defender.eliteModifier, skill?.range ?? 1);
      if (thornDmg > 0 && attacker.isAlive) {
        attacker.stats.hp = Math.max(0, attacker.stats.hp - thornDmg);
        this.showDamageNumber(attacker.position, thornDmg);
        if (attacker.stats.hp <= 0 && attacker.isAlive) {
          attacker.isAlive = false;
          this.sfx.death();
          if (attacker.isPlayer) this.run.killTeamMember(attacker.id);
        }
      }

      // Passif Brute : immobilisation — le joueur perd 1 PM au tour suivant
      if (!attacker.isPlayer && attacker.enemyType === 'brute' && defender.isPlayer && damage > 0) {
        this.addDebuff(defender.id, 'immobilized');
      }

      // Tracking meta stats
      if (attacker.isPlayer) {
        this.run.getMeta().addStat('damageDealt', damage);
      } else {
        this.run.getMeta().addStat('damageTaken', damage);
      }

      const attackerName = attacker.isPlayer ? this.player.name : attacker.name;
      const defenderName = defender.isPlayer ? this.player.name : defender.name;
      const skillLabel = skill ? ` ${skill.name}` : '';
      const totalDef = defender.stats.defense + defenseBonus;
      const defLabel = totalDef > 0 ? ` (${attacker.stats.attack} - ${totalDef} DEF)` : '';
      this.setLog(`${attackerName}${skillLabel} → ${damage} dégâts${defLabel} à ${defenderName}`);

      // SFX selon le skill
      if (skill?.id === 'cannon') this.sfx.cannon();
      else if (skill?.id === 'savage') this.sfx.savage();
      else this.sfx.hit();

      this.flashSprite(targetSprite, defender);
      this.showDamageNumber(defender.position, damage);
      this.screenShakeDirectional(skill?.id === 'savage' ? 6 : 3, attacker.position, defender.position);

      // Particules : sang à l'impact (toutes attaques), explosion sur Frappe Sauvage
      const targetPixel = this.gridToPixel(defender.position);
      if (skill?.id === 'savage') {
        fxExplosion(this, targetPixel.x, targetPixel.y);
      } else {
        fxBlood(this, targetPixel.x, targetPixel.y);
      }

      // Modificateur d'élite — Résurrection : première mort refusée, HP restauré à 50%
      const reviveResult = applyReviveHook(defender.stats.hp, defender.stats.maxHp, defender.eliteModifier);
      if (reviveResult.hp > defender.stats.hp) {
        const reviveHeal = reviveResult.hp - defender.stats.hp;
        defender.stats.hp = reviveResult.hp;
        defender.eliteModifier = reviveResult.modifier;
        // Concatène avec le log de dégâts pour ne PAS écraser le -X dégâts
        this.setLog(`${attackerName}${skillLabel} → ${damage} dégâts à ${defenderName} — 💀 ${defenderName} refuse de mourir et se relève (+${reviveHeal} HP) !`);
        this.screenShake(8);
        // Aura de soin verte + nombre +N pour signaler clairement la régen
        this.playHealEffect(targetSprite, defender.position, reviveHeal, defender);
      }

      if (defender.stats.hp <= 0) {
        defender.isAlive = false;
        // Tracking kills + Codex découverte
        if (!defender.isPlayer) {
          this.run.getMeta().addStat('enemiesKilled');
          if (defender.enemyType) new CodexManager().discoverEnemy(defender.enemyType);
          if (defender.enemyType === 'boss' || defender.enemyType === 'boss_kraken' || defender.enemyType === 'boss_voodoo' || defender.enemyType === 'boss_jack') {
            this.run.getMeta().addStat('bossesKilled');
          }
          // Sprint 82 v3 — Captain Jack vaincu : ajout des 2 items légendaires au coffre + flag
          if (defender.enemyType === 'boss_jack') {
            try { localStorage.setItem('deadtide_jack_defeated', '1'); } catch { /* */ }
            this.run.getCamp().addItemToStorage('jack_sabre_pistol');
            this.run.getCamp().addItemToStorage('jack_ring');
            this.setLog(`🏆 ${defender.name} vaincu ! Trophées légendaires ajoutés au coffre !`);
          }
        }
        this.sfx.death();

        // Bomber : explosion à la mort (2 dégâts en zone 3x3)
        if (!defender.isPlayer && defender.enemyType === 'bomber') {
          // Particules d'explosion centrées sur le bomber
          const bomberPixel = this.gridToPixel(defender.position);
          fxExplosion(this, bomberPixel.x, bomberPixel.y);

          const allInRange = [...this.players, ...this.enemies].filter(c =>
            c.isAlive && c.id !== defender.id
            && Math.abs(c.position.col - defender.position.col) <= 1
            && Math.abs(c.position.row - defender.position.row) <= 1
          );
          for (const c of allInRange) {
            c.stats.hp = Math.max(0, c.stats.hp - 2);
            this.showDamageNumber(c.position, 2);
            if (c.stats.hp <= 0) {
              c.isAlive = false;
              if (c.isPlayer) this.run.killTeamMember(c.id);
              else this.run.getMeta().addStat('enemiesKilled');
            }
          }
          if (allInRange.length > 0) {
            this.setLog(`💥 ${defender.name} explose ! 2 dégâts en zone !`);
            this.screenShake(8);
          }
        }

        this.playKillImpact(targetSprite, defender, () => {
          this.playDeathAnimation(targetSprite, () => onComplete());
        });
      } else if (skill && skill.knockback > 0 && !defender.isPlayer) {
        // Attendre la fin du flash avant de lancer le knockback (évite les conflits de tweens)
        this.time.delayedCall(200, () => {
          this.applyKnockback(attacker, defender, skill.knockback, onComplete);
        });
      } else {
        this.time.delayedCall(200, onComplete);
      }

      this.refreshUI();
    };

    // Animation pré-attaque selon le type de skill
    if (skill?.id === 'cannon') {
      this.playProjectile(attacker.position, defender.position, applyDamage);
    } else if (skill?.id === 'savage') {
      this.playLunge(attackerSprite, attacker.position, defender.position, applyDamage);
    } else {
      // Mêlée basique ou attaque ennemi : petit lunge
      this.playLunge(attackerSprite, attacker.position, defender.position, applyDamage);
    }
  }

  // Projectile animé (boulet de canon) de A vers B
  private playProjectile(from: GridPosition, to: GridPosition, onHit: () => void): void {
    const startPixel = this.gridToPixel(from);
    const endPixel = this.gridToPixel(to);

    const bullet = this.add.circle(startPixel.x, startPixel.y, 5, 0xf39c12);
    bullet.setDepth(10);

    // Traînée
    const trail = this.add.circle(startPixel.x, startPixel.y, 3, 0xe67e22, 0.5);
    trail.setDepth(9);

    this.tweens.add({
      targets: [bullet, trail],
      x: endPixel.x,
      y: endPixel.y,
      duration: 250,
      ease: 'Linear',
      onComplete: () => {
        bullet.destroy();
        trail.destroy();
        onHit();
      },
    });

    // La traînée est en retard
    this.tweens.add({
      targets: trail,
      x: endPixel.x,
      y: endPixel.y,
      duration: 300,
      ease: 'Linear',
      onComplete: () => trail.destroy(),
    });
  }

  // Lunge : le sprite avance vers la cible puis revient
  private playLunge(sprite: CombatSprite, from: GridPosition, toward: GridPosition, onHit: () => void): void {
    const fromPixel = this.gridToPixel(from);
    const towardPixel = this.gridToPixel(toward);

    // Se déplacer de 30% vers la cible
    const lungeX = fromPixel.x + (towardPixel.x - fromPixel.x) * 0.3;
    const lungeY = fromPixel.y + (towardPixel.y - fromPixel.y) * 0.3;

    this.tweens.add({
      targets: sprite,
      x: lungeX,
      y: lungeY,
      duration: 80,
      ease: 'Power2',
      onComplete: () => {
        onHit();
        // Retour à la position d'origine
        this.tweens.add({
          targets: sprite,
          x: fromPixel.x,
          y: fromPixel.y,
          duration: 120,
          ease: 'Power2',
        });
      },
    });
  }

  // Animation de mort : fadeOut + légère montée (300ms)
  /**
   * Animation de soin : aura verte qui s'étend, glow sur le sprite,
   * particules "+" qui montent, et nombre de soin vert flottant.
   * Utilisé par : Soin Ciblé (medic), Potion de Rhum, Élixir Vaudou, Bandage, Dés Truqués.
   */
  private playHealEffect(sprite: CombatSprite, position: GridPosition, healAmount: number, combatant: CombatantState): void {
    const t = sprite as Phaser.GameObjects.Components.Transform;

    // Glow vert sur le sprite (tint temporaire). Pour les rectangles, on flash le contour.
    if (sprite instanceof Phaser.GameObjects.Sprite) {
      sprite.setTint(0x88ff88);
      this.time.delayedCall(260, () => {
        sprite.clearTint();
        // Réappliquer la teinte HP (au cas où le soin a sorti le combattant de la zone rouge)
        this.applyHpTint(sprite, combatant);
      });
    } else if (sprite instanceof Phaser.GameObjects.Rectangle) {
      const orig = sprite.strokeColor;
      sprite.setStrokeStyle(3, 0x2ecc71);
      this.time.delayedCall(260, () => {
        sprite.setStrokeStyle(2, orig);
        this.applyHpTint(sprite, combatant);
      });
    }

    // Aura : cercle vert qui s'étend
    const aura = this.add.circle(t.x, t.y, 8, 0x2ecc71, 0).setStrokeStyle(2, 0x2ecc71, 1).setDepth(50);
    this.tweens.add({
      targets: aura,
      alpha: 0,
      duration: 450,
      ease: 'Power2',
      onUpdate: (tw) => aura.setRadius(8 + 28 * tw.progress),
      onComplete: () => aura.destroy(),
    });

    // 5 sparkles "+" qui montent en éventail
    for (let i = 0; i < 5; i++) {
      const offsetX = Phaser.Math.Between(-14, 14);
      const sparkle = this.add.text(t.x + offsetX, t.y, '+', {
        fontSize: '14px', color: '#2ecc71', fontFamily: FONT_BODY, fontStyle: 'bold',
        stroke: '#0a3a1a', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(60);
      this.tweens.add({
        targets: sparkle,
        y: t.y - 28 - Phaser.Math.Between(0, 12),
        alpha: 0,
        duration: 650 + i * 40,
        ease: 'Sine.easeOut',
        delay: i * 50,
        onComplete: () => sparkle.destroy(),
      });
    }

    // Nombre de soin vert flottant (+N)
    const healText = this.add.text(t.x, t.y - 20, `+${healAmount}`, {
      fontSize: '18px', color: '#2ecc71', fontFamily: FONT_BODY, fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(70);
    this.tweens.add({
      targets: healText,
      y: t.y - 60,
      alpha: 0,
      duration: 800,
      ease: 'Power2',
      onComplete: () => healText.destroy(),
    });
  }

  /**
   * Bobbing idle : léger souffle vertical sur les sprites de combat.
   * Donne vie à la grille même quand rien ne se passe (entre les tours).
   * N'affecte pas la position, uniquement scaleY → ne collisionne pas
   * avec les tweens d'attaque (lunge, knockback) qui touchent x/y.
   */
  private applyIdleBob(sprite: CombatSprite): void {
    const baseScaleY = sprite.scaleY;
    this.tweens.add({
      targets: sprite,
      scaleY: baseScaleY * 1.05,
      duration: Phaser.Math.Between(550, 750),
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: Phaser.Math.Between(0, 500),
    });
  }

  /** Hit pause + zoom au moment du kill : effet "wahou" */
  private playKillImpact(sprite: CombatSprite, defender: CombatantState, onComplete: () => void): void {
    const isBoss = defender.enemyType === 'boss' || defender.enemyType === 'boss_kraken' || defender.enemyType === 'boss_voodoo' || defender.enemyType === 'boss_jack';
    const t = sprite as Phaser.GameObjects.Components.Transform;

    // Shake plus marqué que le shake d'attaque normal
    this.screenShake(isBoss ? 14 : 7);

    // Zoom-in du sprite mourant : effet de focus sans casser l'UI (camera reste fixe)
    const baseScale = (sprite as Phaser.GameObjects.Components.Transform & { scaleX: number }).scaleX || 1;
    const zoomScale = isBoss ? baseScale * 1.6 : baseScale * 1.3;
    this.tweens.add({
      targets: sprite,
      scale: zoomScale,
      duration: isBoss ? 120 : 80,
      yoyo: true,
      ease: 'Sine.easeOut',
    });

    // Flash blanc plein écran sur kill de boss : "freeze frame"
    if (isBoss) {
      const flash = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0xffffff, 0.7).setDepth(200);
      this.tweens.add({
        targets: flash,
        alpha: 0,
        duration: 200,
        ease: 'Power2',
        onComplete: () => flash.destroy(),
      });

      // Onde de choc : cercle qui s'agrandit autour du boss
      const shockwave = this.add.circle(t.x, t.y, 10, 0xffffff, 0).setStrokeStyle(3, 0xffffff, 1).setDepth(150);
      this.tweens.add({
        targets: shockwave,
        alpha: 0,
        duration: 350,
        ease: 'Power2',
        onUpdate: (tw) => shockwave.setRadius(10 + 70 * tw.progress),
        onComplete: () => shockwave.destroy(),
      });
    }

    // Slow-motion sur le coup fatal du boss : ralentit tweens & timers ~400ms réels.
    // setTimeout est en temps réel (non affecté par timeScale) → restoration garantie.
    if (isBoss) {
      this.tweens.timeScale = 0.35;
      this.time.timeScale = 0.35;
      window.setTimeout(() => {
        this.tweens.timeScale = 1;
        this.time.timeScale = 1;
      }, 400);
    }

    // Hit pause : délai avant la death animation (plus long pour boss)
    const pauseDuration = isBoss ? 280 : 110;
    this.time.delayedCall(pauseDuration, onComplete);
  }

  private playDeathAnimation(sprite: CombatSprite, onComplete: () => void): void {
    // Particules de poussière + flash blanc à la position du sprite
    const t = sprite as Phaser.GameObjects.Components.Transform;
    fxDust(this, t.x, t.y);

    this.tweens.add({
      targets: sprite,
      alpha: 0,
      y: t.y - 8,
      duration: 300,
      ease: 'Power2',
      onComplete,
    });
  }

  // Tremblement de caméra
  private screenShake(intensity: number): void {
    this.cameras.main.shake(150, intensity / 1000);
  }

  /**
   * Tremblement directionnel : shake classique + petit décalage caméra dans la
   * direction de l'attaque (renforce la sensation d'impact).
   */
  private screenShakeDirectional(intensity: number, fromPos: GridPosition, toPos: GridPosition): void {
    this.screenShake(intensity);
    const dx = toPos.col - fromPos.col;
    const dy = toPos.row - fromPos.row;
    const len = Math.hypot(dx, dy);
    if (len === 0) return;
    const cam = this.cameras.main;
    const offset = Math.min(6, intensity * 0.6);
    const targetX = cam.scrollX + (dx / len) * offset;
    const targetY = cam.scrollY + (dy / len) * offset;
    this.tweens.add({
      targets: cam,
      scrollX: targetX,
      scrollY: targetY,
      duration: 60,
      yoyo: true,
      ease: 'Power2',
    });
  }

  /**
   * Teinte progressive selon les HP : neutre >50%, orangé 25-50%, rouge <25%.
   * Donne un feedback visuel persistant de l'état de santé.
   */
  private applyHpTint(sprite: CombatSprite, combatant: CombatantState): void {
    if (!combatant.isAlive) return;
    const ratio = combatant.stats.hp / Math.max(1, combatant.stats.maxHp);
    let tint: number | null = null;
    if (ratio < 0.25) tint = 0xff5555;
    else if (ratio < 0.5) tint = 0xffaa66;

    if (sprite instanceof Phaser.GameObjects.Rectangle) {
      // Rectangles : on teinte le contour (le fill reste pour l'identité visuelle)
      const originalOutline = combatant.isPlayer
        ? COLORS.PLAYER_OUTLINE
        : ENEMY_DEFINITIONS[combatant.enemyType!].outline;
      sprite.setStrokeStyle(2, tint ?? originalOutline);
    } else {
      if (tint !== null) (sprite as Phaser.GameObjects.Sprite).setTint(tint);
      else (sprite as Phaser.GameObjects.Sprite).clearTint();
    }
  }

  private applyKnockback(attacker: CombatantState, defender: CombatantState, distance: number, onComplete: () => void): void {
    // Direction du recul : depuis l'attaquant vers le défenseur
    const dx = defender.position.col - attacker.position.col;
    const dy = defender.position.row - attacker.position.row;

    // Normaliser la direction (une seule direction cardinale)
    const dirCol = dx !== 0 ? Math.sign(dx) : 0;
    const dirRow = dy !== 0 ? Math.sign(dy) : 0;

    // Trouver la case de destination (s'arrête aux obstacles)
    let finalPos = { ...defender.position };
    for (let i = 0; i < distance; i++) {
      const nextCol = finalPos.col + dirCol;
      const nextRow = finalPos.row + dirRow;

      if (nextCol < 0 || nextCol >= GRID.COLS || nextRow < 0 || nextRow >= GRID.ROWS) break;
      if (this.isOccupiedExcept(nextCol, nextRow, defender.id)) break;

      finalPos = { col: nextCol, row: nextRow };
    }

    // Animer le recul si la position a changé (pas d'AoO sur le knockback)
    if (finalPos.col !== defender.position.col || finalPos.row !== defender.position.row) {
      this.isKnockbackMove = true;
      const sprite = this.enemySprites.get(defender.id)!;
      // Stopper tout tween en cours sur ce sprite avant le knockback
      this.tweens.killTweensOf(sprite);
      this.moveEntity(sprite, defender, finalPos, () => {
        this.isKnockbackMove = false;
        onComplete();
      });
    } else {
      this.isKnockbackMove = false;
      onComplete();
    }
  }

  private flashSprite(sprite: CombatSprite, combatant: CombatantState): void {
    if (sprite instanceof Phaser.GameObjects.Rectangle) {
      let originalColor: number;
      if (combatant.isPlayer) {
        originalColor = COLORS.PLAYER;
      } else {
        originalColor = ENEMY_DEFINITIONS[combatant.enemyType!].color;
      }
      sprite.setFillStyle(0xffffff);
      this.time.delayedCall(120, () => {
        (sprite as Phaser.GameObjects.Rectangle).setFillStyle(originalColor);
        // Réappliquer la teinte HP après le flash (rectangles : on tinte légèrement le contour)
        this.applyHpTint(sprite, combatant);
      });
    } else {
      // Flash blanc via tint pour les sprites SVG
      sprite.setTint(0xffffff);
      this.time.delayedCall(120, () => {
        (sprite as Phaser.GameObjects.Sprite).clearTint();
        this.applyHpTint(sprite, combatant);
      });
    }

    // Pulse de scale pour renforcer l'impact (1.0 → 1.15 → 1.0 sur 100ms)
    const baseScaleX = sprite.scaleX;
    const baseScaleY = sprite.scaleY;
    this.tweens.add({
      targets: sprite,
      scaleX: baseScaleX * 1.15,
      scaleY: baseScaleY * 1.15,
      duration: 50,
      yoyo: true,
      ease: 'Sine.easeOut',
    });

    // Léger shake horizontal (conservé pour la lisibilité du coup)
    this.tweens.add({
      targets: sprite,
      x: sprite.x + 4,
      duration: 50,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.easeInOut',
    });
  }

  private showDamageNumber(pos: GridPosition, damage: number): void {
    const pixel = this.gridToPixel(pos);
    const dmgText = this.add.text(pixel.x, pixel.y - 20, `-${damage}`, {
      fontSize: '18px',
      color: '#e74c3c',
      fontFamily: FONT_BODY,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    });
    dmgText.setOrigin(0.5);

    this.tweens.add({
      targets: dmgText,
      y: pixel.y - 60,
      alpha: 0,
      duration: 800,
      ease: 'Power2',
      onComplete: () => dmgText.destroy(),
    });
  }

  // ===========================
  // GAME OVER
  // ===========================

  private gameOver(victory: boolean): void {
    this.phase = victory ? 'victory' : 'defeat';
    this.clearHighlights();
    // LOT B3 : forcer la fermeture des overlays/tooltips qui pourraient rester
    // visibles à travers la popup Victoire (overlay 60% opaque transparent)
    this.tooltip?.setVisible(false);
    this.refreshUI();

    // Jingle victoire ou défaite
    if (victory) {
      MusicManager.playJingle(this, 'victory');
    } else {
      MusicManager.playJingle(this, 'defeat');
    }

    // Sauvegarder les HP de toute l'équipe
    if (victory) {
      for (const p of this.players) {
        this.run.setTeamMemberHp(p.id, p.isAlive ? p.stats.hp : 0);
        if (!p.isAlive) this.run.killTeamMember(p.id);
      }
      // Rétro-compat : sauver HP du premier joueur vivant
      const firstAlive = this.players.find(p => p.isAlive);
      if (firstAlive) this.run.setPlayerHp(firstAlive.stats.hp);
      // Récompense or (scaling par étage : +2 or par étage)
      const nodeType = this.run.getCurrentNodeType();
      const currentNode = this.run.getMap().nodes.find(n => n.id === this.run.getMap().currentNodeId);
      const floorBonus = (currentNode?.floor ?? 0) * 2;
      const goldReward = (nodeType === 'boss' ? 25 : nodeType === 'elite' ? 15 : 8) + floorBonus;
      this.run.addGold(goldReward);
      // Récompense ressources (multiplicateur par acte : Acte 2 ×1.5, Acte 3 ×2)
      const resDrop = nodeType === 'boss'
        ? rollCombatBossResources()
        : nodeType === 'elite'
          ? rollCombatEliteResources()
          : rollCombatNormalResources();
      const actFloor = currentNode?.floor ?? 0;
      const actMultiplier = actFloor >= 8 ? 2 : actFloor >= 5 ? 1.5 : 1;
      if (actMultiplier > 1) {
        for (const key of Object.keys(resDrop) as (keyof typeof resDrop)[]) {
          if (resDrop[key]) resDrop[key] = Math.ceil(resDrop[key]! * actMultiplier);
        }
      }
      this.run.getCamp().getResources().addMultiple(resDrop);
      this._lastResourceDrop = resDrop;
    } else {
      // Défaite — tuer tous les membres
      for (const p of this.players) {
        this.run.killTeamMember(p.id);
      }
    }

    const color = victory ? '#2ecc71' : '#e74c3c';
    this.setLog(victory ? 'Victoire !' : 'Défaite...');

    // Vérifier les nouveaux achievements
    const newAchievements = this.run.getMeta().checkAchievements();

    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.6);

    // Afficher les achievements débloqués
    if (newAchievements.length > 0) {
      let achY = GAME_CY + 50;
      for (const achId of newAchievements) {
        const ach = ACHIEVEMENTS.find(a => a.id === achId);
        if (!ach) continue;
        const unlockLabel = ach.unlockItemId
          ? ` → Débloque : ${ITEM_DEFINITIONS[ach.unlockItemId]?.emoji ?? ''} ${ITEM_DEFINITIONS[ach.unlockItemId]?.name ?? ach.unlockItemId}`
          : '';
        const toast = this.add.text(GAME_CX, achY, `🏅 ${ach.emoji} ${ach.name} — ${ach.description}${unlockLabel}`, {
          fontSize: '12px', color: '#f1c40f', fontFamily: FONT_BODY,
          backgroundColor: '#2c2a0a', padding: { x: 10, y: 5 },
          wordWrap: { width: Math.min(500, GAME_W - 30) },
        }).setOrigin(0.5).setAlpha(0);
        this.tweens.add({
          targets: toast,
          alpha: 1,
          y: achY - 10,
          duration: 600,
          ease: 'Power2',
        });
        achY += 30;
      }
    }

    if (victory) {
      const isRunDone = this.run.isLastCombat();
      const message = isRunDone
        ? '🏆 EXPÉDITION TERMINÉE !\nLe Capitaine Maudit est vaincu !'
        : `🏆 VICTOIRE !\n${this.run.getCurrentFloorLabel()} terminé`;

      this.add.text(GAME_CX, GAME_CY - 100, message, {
        fontSize: '20px', color, fontFamily: FONT_BODY, fontStyle: 'bold',
        align: 'center', stroke: '#000000', strokeThickness: 4,
        wordWrap: { width: Math.min(500, GAME_W - 30) },
      }).setOrigin(0.5);

      // Afficher le drop de ressources
      const resLabel = formatResourceDrop(this._lastResourceDrop);
      if (resLabel) {
        this.add.text(GAME_CX, GAME_CY - 50, resLabel, {
          fontSize: '14px', color: '#f39c12', fontFamily: FONT_BODY,
          backgroundColor: '#1a1a0a', padding: { x: 8, y: 4 },
        }).setOrigin(0.5);
      }

      if (isRunDone) {
        // Boss final → loot d'abord, puis DIRECTEMENT RunVictoryScene
        // (skip EquipScene pour éviter le blocage en MapScene vide).
        // Bug fix v0.36.8 : avant on passait par LootScene → EquipScene → MapScene
        // mais MapScene n'a pas de logique "fin de map" → joueur bloqué.
        this.run.completeCurrentNode();
        const endBtn = this.add.text(GAME_CX, GAME_CY - 10, '[ Voir le butin final → ]', {
          fontSize: '16px', color: '#f1c40f', fontFamily: FONT_BODY,
          backgroundColor: '#2c3e50', padding: { x: 16, y: 8 },
        }).setOrigin(0.5);
        endBtn.setInteractive({ useHandCursor: true });
        endBtn.on('pointerdown', () => {
          startSceneWithFade(this, 'LootScene', { run: this.run, nextSceneAfter: 'RunVictoryScene' }, 'radial');
        });
        endBtn.on('pointerover', () => endBtn.setColor('#ffffff'));
        endBtn.on('pointerout', () => endBtn.setColor('#f1c40f'));
      } else {
        // Victoire → loot puis retour à la carte
        this.run.completeCurrentNode();
        const lootBtn = this.add.text(GAME_CX, GAME_CY - 10, '[ Voir le butin → ]', {
          fontSize: '16px', color: '#f1c40f', fontFamily: FONT_BODY,
          backgroundColor: '#2c3e50', padding: { x: 16, y: 8 },
        }).setOrigin(0.5);
        lootBtn.setInteractive({ useHandCursor: true });
        lootBtn.on('pointerdown', () => {
          startSceneWithFade(this, 'LootScene', { run: this.run });
        });
        lootBtn.on('pointerover', () => lootBtn.setColor('#ffffff'));
        lootBtn.on('pointerout', () => lootBtn.setColor('#f1c40f'));
      }
    } else {
      // Défaite → permadeath, restart
      this.add.text(GAME_CX, GAME_CY - 90, '💀 DÉFAITE...\nTon capitaine est tombé.\nTout est perdu.', {
        fontSize: '20px', color, fontFamily: FONT_BODY, fontStyle: 'bold',
        align: 'center', stroke: '#000000', strokeThickness: 4,
      }).setOrigin(0.5);

      const retryBtn = this.add.text(GAME_CX, GAME_CY, '[ Retour au Camp ]', {
        fontSize: '16px', color: '#f1c40f', fontFamily: FONT_BODY,
        backgroundColor: '#2c3e50', padding: { x: 16, y: 8 },
      }).setOrigin(0.5);
      retryBtn.setInteractive({ useHandCursor: true });
      retryBtn.on('pointerdown', () => {
        this.run.getMeta().endRun(false);
        RunManager.clearSavedRun();
        startSceneWithFade(this, 'RunEndScene', { run: this.run, victory: false });
      });
      retryBtn.on('pointerover', () => retryBtn.setColor('#ffffff'));
      retryBtn.on('pointerout', () => retryBtn.setColor('#f1c40f'));
    }
  }

  // ===========================
  // CALCUL CASES ACCESSIBLES
  // ===========================

  private calculateReachableTiles(): void {
    this.movableTiles = [];
    this.attackableTiles = [];

    const pCol = this.player.position.col;
    const pRow = this.player.position.row;

    // Cases de déplacement
    if (this.player.currentPm > 0) {
      for (let row = 0; row < GRID.ROWS; row++) {
        for (let col = 0; col < GRID.COLS; col++) {
          const dist = this.gridDistance({ col: pCol, row: pRow }, { col, row });
          if (dist > 0 && dist <= this.player.currentPm && !this.isOccupied(col, row)) {
            this.movableTiles.push({ col, row });
          }
        }
      }
    }

    // Cases d'attaque/ciblage selon le skill sélectionné
    if (this.player.currentPa >= this.selectedSkill.paCost) {
      const totalRange = this.selectedSkill.range + (this.selectedSkill.targetAlly ? 0 : this.getPlayerBonusRange());

      if (this.selectedSkill.targetAlly) {
        // Skills ciblant les alliés (soin, purification)
        for (const p of this.players) {
          if (!p.isAlive) continue;
          const dist = this.gridDistance(this.player.position, p.position);
          if (dist <= totalRange) {
            this.attackableTiles.push({ ...p.position });
          }
        }
      } else if (this.selectedSkill.special === 'shield') {
        // Mur de Bouclier : cibler sa propre case
        this.attackableTiles.push({ ...this.player.position });
      } else {
        // Skills offensifs normaux
        const minRange = this.selectedSkill.minRange ?? 1;
        for (const enemy of this.enemies) {
          if (!enemy.isAlive) continue;
          const dist = this.gridDistance(this.player.position, enemy.position);
          if (dist >= minRange && dist <= totalRange) {
            if (totalRange <= 1 || this.hasLineOfSight(this.player.position, enemy.position, enemy.id)) {
              this.attackableTiles.push({ ...enemy.position });
            }
          }
        }
      }
    }
  }

  // ===========================
  // HIGHLIGHT GRILLE
  // ===========================

  private highlightTiles(): void {
    this.clearHighlights();
    for (const pos of this.movableTiles) {
      // Vérifier si ce mouvement déclenche un AoO (orange = danger)
      const threats = this.getAoOThreats(this.player, pos);
      // Vérifier si un crabe bloque la fuite (rouge clignotant)
      const crabBlock = this.enemies.some(e =>
        e.isAlive && e.enemyType === 'crab'
        && this.gridDistance(this.player.position, e.position) === 1
        && this.gridDistance(pos, e.position) > 1
      );
      if (crabBlock) {
        this.tiles[pos.row][pos.col].setFillStyle(0x8b0000, 0.3); // rouge foncé = bloqué
      } else if (threats.length > 0) {
        this.tiles[pos.row][pos.col].setFillStyle(0xe67e22, 0.5); // orange = AoO
      } else {
        this.tiles[pos.row][pos.col].setFillStyle(COLORS.TILE_MOVE, 0.4);
      }
    }
    for (const pos of this.attackableTiles) {
      this.tiles[pos.row][pos.col].setFillStyle(COLORS.TILE_ATTACK, 0.5);
    }
  }

  private clearHighlights(): void {
    for (let row = 0; row < GRID.ROWS; row++) {
      for (let col = 0; col < GRID.COLS; col++) {
        const color = (row + col) % 2 === 0 ? COLORS.TILE_LIGHT : COLORS.TILE_DARK;
        this.tiles[row][col].setFillStyle(color);
      }
    }
  }

  private refreshTileColor(col: number, row: number): void {
    if (this.isInList(this.movableTiles, col, row)) {
      this.tiles[row][col].setFillStyle(COLORS.TILE_MOVE, 0.4);
    } else if (this.isInList(this.attackableTiles, col, row)) {
      this.tiles[row][col].setFillStyle(COLORS.TILE_ATTACK, 0.5);
    } else {
      const color = (row + col) % 2 === 0 ? COLORS.TILE_LIGHT : COLORS.TILE_DARK;
      this.tiles[row][col].setFillStyle(color);
    }
  }

  private isTileHighlighted(col: number, row: number): boolean {
    return this.isInList(this.movableTiles, col, row) || this.isInList(this.attackableTiles, col, row);
  }

  private getHighlightColor(col: number, row: number): number {
    if (this.isInList(this.attackableTiles, col, row)) return COLORS.TILE_ATTACK;
    return COLORS.TILE_MOVE;
  }

  // ===========================
  // MOUVEMENT ANIMÉ
  // ===========================

  private moveEntity(
    sprite: CombatSprite,
    combatant: CombatantState,
    target: GridPosition,
    onComplete: () => void
  ): void {
    combatant.position = { ...target };
    const pixel = this.gridToPixel(target);

    this.tweens.add({
      targets: sprite,
      x: pixel.x,
      y: pixel.y,
      duration: 200,
      ease: 'Power2',
      onComplete,
    });

    // Animer aussi l'emoji de classe si c'est un joueur
    const emoji = this.playerEmojis.get(combatant.id);
    if (emoji) {
      this.tweens.add({
        targets: emoji,
        x: pixel.x,
        y: pixel.y - GRID.TILE_SIZE / 2 - 6,
        duration: 200,
        ease: 'Power2',
      });
    }

    // Animer le name tag ennemi
    const nameTag = this.children.getByName(`enemy_name_${combatant.id}`) as Phaser.GameObjects.Text;
    if (nameTag) {
      this.tweens.add({
        targets: nameTag,
        x: pixel.x,
        y: pixel.y - GRID.TILE_SIZE / 2 - 4,
        duration: 200,
        ease: 'Power2',
      });
    }
  }

  // ===========================
  // PATHFINDING SIMPLE
  // ===========================

  /** BFS pour trouver un chemin vers la cible (ou le plus proche possible), en contournant les obstacles */
  private findPathToward(from: GridPosition, to: GridPosition, maxSteps: number, ignoreEnemyCollision = false): GridPosition[] {
    // BFS sur la grille : trouver le chemin le plus court vers une case adjacente à la cible
    const key = (p: GridPosition) => `${p.col},${p.row}`;
    const cameFrom = new Map<string, GridPosition | null>();
    const queue: GridPosition[] = [from];
    cameFrom.set(key(from), null);

    let bestEnd: GridPosition | null = null;
    let bestDist = this.gridDistance(from, to);

    while (queue.length > 0) {
      const current = queue.shift()!;
      const currentKey = key(current);

      // Reconstruire la longueur du chemin jusqu'ici
      let pathLen = 0;
      let trace = currentKey;
      while (cameFrom.get(trace) !== null && cameFrom.has(trace)) {
        pathLen++;
        const prev = cameFrom.get(trace)!;
        trace = key(prev);
      }

      // Ne pas explorer au-delà de la portée de déplacement + marge pour trouver un contournement
      if (pathLen > maxSteps + 4) continue;

      const dist = this.gridDistance(current, to);
      if (dist < bestDist || (dist === bestDist && bestEnd === null)) {
        bestDist = dist;
        bestEnd = current;
      }

      // Adjacent à la cible = trouvé
      if (dist <= 1 && !(current.col === from.col && current.row === from.row)) {
        bestEnd = current;
        break;
      }

      for (const n of this.getNeighbors(current)) {
        const nk = key(n);
        if (cameFrom.has(nk)) continue;
        // Bloquer les cases occupées (sauf la cible elle-même, et les ennemis si ignoreEnemyCollision)
        if (this.isOccupied(n.col, n.row) && !(n.col === to.col && n.row === to.row)) {
          if (!ignoreEnemyCollision) continue;
          // Si on ignore les collisions ennemies, ne bloquer que les joueurs
          const hasPlayer = this.players.some(p => p.isAlive && p.position.col === n.col && p.position.row === n.row);
          if (hasPlayer) continue;
        }
        cameFrom.set(nk, current);
        queue.push(n);
      }
    }

    if (!bestEnd || (bestEnd.col === from.col && bestEnd.row === from.row)) return [];

    // Reconstruire le chemin
    const fullPath: GridPosition[] = [];
    let cur: GridPosition | null = bestEnd;
    while (cur && !(cur.col === from.col && cur.row === from.row)) {
      fullPath.unshift(cur);
      cur = cameFrom.get(key(cur)) ?? null;
    }

    // Limiter à maxSteps
    const truncated = fullPath.slice(0, maxSteps);
    // Ghost-move : traverser les ennemis OK, mais s'arrêter sur leur case provoque
    // une superposition (bug v0.38.4 — Rôdeur spectral landing sur boss/zombie).
    if (ignoreEnemyCollision) {
      while (truncated.length > 0) {
        const last = truncated[truncated.length - 1];
        if (!this.isOccupied(last.col, last.row)) break;
        truncated.pop();
      }
    }
    return truncated;
  }

  // ===========================
  // UTILITAIRES
  // ===========================

  // Ligne de vue (Bresenham simplifié) : vérifie qu'aucun ennemi vivant ne bloque le chemin
  private hasLineOfSight(from: GridPosition, to: GridPosition, targetId: string): boolean {
    let x0 = from.col;
    let y0 = from.row;
    const x1 = to.col;
    const y1 = to.row;

    const dx = Math.abs(x1 - x0);
    const dy = Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx - dy;

    while (x0 !== x1 || y0 !== y1) {
      const e2 = 2 * err;
      if (e2 > -dy) { err -= dy; x0 += sx; }
      if (e2 < dx) { err += dx; y0 += sy; }

      // On est arrivé à la cible, c'est bon
      if (x0 === x1 && y0 === y1) return true;

      // Case intermédiaire occupée par un autre ennemi vivant = bloqué
      for (const enemy of this.enemies) {
        if (enemy.id === targetId || !enemy.isAlive) continue;
        if (enemy.position.col === x0 && enemy.position.row === y0) return false;
      }
    }

    return true;
  }

  private gridToPixel(pos: GridPosition): { x: number; y: number } {
    return {
      x: GRID.ORIGIN_X + pos.col * GRID.TILE_SIZE + GRID.TILE_SIZE / 2,
      y: GRID.ORIGIN_Y + pos.row * GRID.TILE_SIZE + GRID.TILE_SIZE / 2,
    };
  }

  // === ATTAQUE D'OPPORTUNITÉ (AoO) ===

  /**
   * Vérifie et applique les AoO quand un combattant quitte une case adjacente à un ennemi.
   * Retourne les dégâts totaux infligés. Callback appelé après toutes les animations.
   */
  private checkAndApplyAoO(
    mover: CombatantState,
    oldPos: GridPosition,
    newPos: GridPosition,
    onComplete: () => void,
  ): void {
    if (this.isKnockbackMove) { onComplete(); return; }
    if (!mover.isAlive) { onComplete(); return; }

    // Trouver les adversaires qui étaient adjacents à l'ancienne position
    const opponents = mover.isPlayer ? this.enemies : this.players;
    const threateners = opponents.filter(o =>
      o.isAlive && this.gridDistance(oldPos, o.position) === 1
      // Ne déclenche que si le mouvement éloigne (pas si on reste adjacent)
      && this.gridDistance(newPos, o.position) > 1
    );

    if (threateners.length === 0) { onComplete(); return; }

    // Appliquer les AoO séquentiellement
    let i = 0;
    const nextAoO = () => {
      if (i >= threateners.length || !mover.isAlive) { onComplete(); return; }
      const attacker = threateners[i++];
      const aooDamage = Math.max(1, Math.round(attacker.stats.attack * 0.75) - mover.stats.defense);
      mover.stats.hp = Math.max(0, mover.stats.hp - aooDamage);

      // Feedback visuel
      this.showDamageNumber(mover.position, aooDamage);
      const moverSprite = mover.isPlayer
        ? this.playerSprites.get(mover.id)
        : this.enemySprites.get(mover.id);
      if (moverSprite) this.flashSprite(moverSprite, mover);
      this.setLog(`⚡ Tacle ! ${attacker.name} frappe ${mover.name} (${aooDamage} dégâts)`);
      this.sfx.hit();
      this.screenShake(2);

      // Vérifier mort
      if (mover.stats.hp <= 0) {
        mover.isAlive = false;
        if (moverSprite) {
          this.playDeathAnimation(moverSprite, () => {});
        }
        if (mover.isPlayer) {
          this.run.killTeamMember(mover.id);
          this.setLog(`💀 ${mover.name} est tombé (tacle) !`);
        } else {
          this.run.getMeta().addStat('enemiesKilled');
          this.setLog(`💀 ${mover.name} abattu (tacle) !`);
        }
      }

      this.refreshUI();
      this.time.delayedCall(300, nextAoO);
    };
    nextAoO();
  }

  /** Retourne les ennemis qui taclent si le joueur bouge à cette position (pour le highlight) */
  private getAoOThreats(mover: CombatantState, targetPos: GridPosition): CombatantState[] {
    const opponents = mover.isPlayer ? this.enemies : this.players;
    return opponents.filter(o =>
      o.isAlive && this.gridDistance(mover.position, o.position) === 1
      && this.gridDistance(targetPos, o.position) > 1
    );
  }

  /** Feedback visuel + sonore quand le joueur tente une action sans PA suffisants */
  private showInsufficientPaFeedback(): void {
    this.sfx.error();
    // Flash rouge sur le texte de stats
    this.statsText.setColor('#e74c3c');
    this.time.delayedCall(300, () => this.statsText.setColor('#ecf0f1'));
    // Shake horizontal sur le bouton de skill actif (LOT B1 anti-drift)
    const btnIdx = this.activeSkills.indexOf(this.selectedSkill);
    const btn = this.skillButtons[btnIdx];
    if (btn) {
      // Position d'origine stockée au mount (anti-drift) — sinon capturer btn.x
      // pendant un tween en cours capture une valeur erronée et le bouton dérive.
      const baseX = (btn as Phaser.GameObjects.Text & { _baseX?: number })._baseX ?? btn.x;
      // Tuer tout tween en cours sur ce bouton avant d'en démarrer un nouveau
      this.tweens.killTweensOf(btn);
      btn.setX(baseX);
      this.tweens.add({
        targets: btn,
        x: baseX + 5,
        duration: 40,
        yoyo: true,
        repeat: 3,
        onComplete: () => btn.setX(baseX),
      });
    }
  }

  /** Reset défensif des positions des boutons de skill (anti-drift au début de tour) */
  private resetSkillButtonPositions(): void {
    for (const btn of this.skillButtons) {
      this.tweens.killTweensOf(btn);
      const baseX = (btn as Phaser.GameObjects.Text & { _baseX?: number })._baseX;
      const baseY = (btn as Phaser.GameObjects.Text & { _baseY?: number })._baseY;
      if (baseX !== undefined) btn.setX(baseX);
      if (baseY !== undefined) btn.setY(baseY);
    }
  }

  /** Vérifie si un combattant a le debuff donné */
  private hasDebuff(combatantId: string, debuff: string): boolean {
    return this.debuffs.get(combatantId)?.has(debuff) ?? false;
  }

  /** Ajoute un debuff à un combattant */
  private addDebuff(combatantId: string, debuff: string): void {
    if (!this.debuffs.has(combatantId)) this.debuffs.set(combatantId, new Set());
    this.debuffs.get(combatantId)!.add(debuff);
  }

  /** Retire un debuff */
  private removeDebuff(combatantId: string, debuff: string): void {
    this.debuffs.get(combatantId)?.delete(debuff);
  }

  private gridDistance(a: GridPosition, b: GridPosition): number {
    return Math.abs(a.col - b.col) + Math.abs(a.row - b.row);
  }

  private isAdjacent(a: GridPosition, b: GridPosition): boolean {
    return this.gridDistance(a, b) === 1;
  }

  private isOccupied(col: number, row: number): boolean {
    for (const p of this.players) {
      if (p.isAlive && p.position.col === col && p.position.row === row) return true;
    }
    for (const enemy of this.enemies) {
      if (enemy.isAlive && enemy.position.col === col && enemy.position.row === row) return true;
    }
    return false;
  }

  // Comme isOccupied mais exclut un combattant (pour le knockback)
  private isOccupiedExcept(col: number, row: number, exceptId: string): boolean {
    for (const p of this.players) {
      if (p.id === exceptId) continue;
      if (p.isAlive && p.position.col === col && p.position.row === row) return true;
    }
    for (const enemy of this.enemies) {
      if (enemy.id === exceptId) continue;
      if (enemy.isAlive && enemy.position.col === col && enemy.position.row === row) return true;
    }
    return false;
  }

  /** Retourne la classe d'un joueur (par son CombatantState) */
  private getPlayerClass(p: CombatantState): SurvivorClass {
    const team = this.run.getTeam();
    const member = team.find(t => t.id === p.id);
    return member?.class ?? this.run.getSelectedClass();
  }

  /** Vérifie si tous les joueurs sont morts */
  private allPlayersDead(): boolean {
    return this.players.every(p => !p.isAlive);
  }

  private getEnemyAt(col: number, row: number): CombatantState | null {
    return this.enemies.find(e => e.isAlive && e.position.col === col && e.position.row === row) ?? null;
  }

  private allEnemiesDead(): boolean {
    return this.enemies.every(e => !e.isAlive);
  }

  /** Bonus de portée total : équipement + classe (ranger +1) */
  private getPlayerBonusRange(): number {
    const equipRange = this.inventory.getBonusRange(this.player.id);
    const playerClass = this.getPlayerClass(this.player);
    const classBonus = playerClass === 'ranger' ? 1 : 0;
    return equipRange + classBonus;
  }

  private getNeighbors(pos: GridPosition): GridPosition[] {
    const dirs = [
      { col: 0, row: -1 },
      { col: 0, row: 1 },
      { col: -1, row: 0 },
      { col: 1, row: 0 },
    ];
    return dirs
      .map((d) => ({ col: pos.col + d.col, row: pos.row + d.row }))
      .filter((p) => p.col >= 0 && p.col < GRID.COLS && p.row >= 0 && p.row < GRID.ROWS);
  }

  private isInList(list: GridPosition[], col: number, row: number): boolean {
    return list.some((p) => p.col === col && p.row === row);
  }

  // === LONG PRESS MOBILE : afficher info sur entité/case ===
  private handleLongPress(px: number, py: number): void {
    // Convertir la position pixel en coordonnées de grille
    const col = Math.floor((px - GRID.ORIGIN_X) / GRID.TILE_SIZE);
    const row = Math.floor((py - GRID.ORIGIN_Y) / GRID.TILE_SIZE);
    if (col < 0 || col >= GRID.COLS || row < 0 || row >= GRID.ROWS) return;

    // Chercher un ennemi sur cette case
    const enemy = this.enemies.find(e => e.isAlive && e.position.col === col && e.position.row === row);
    if (enemy) {
      const def = ENEMY_DEFINITIONS[enemy.enemyType!];
      const rawDmg = Math.max(1, this.player.stats.attack - enemy.stats.defense);
      const skillDmg = this.selectedSkill ? Math.max(1, Math.round(rawDmg * this.selectedSkill.damage)) : rawDmg;
      const passifs: Record<string, string> = {
        zombie: '🧟 Meute: +1 ATK/zombie adjacent',
        brute: '⛓️ Immobilisation: -1 PM cible',
        crab: '🦀 Étreinte: bloque la fuite',
        shark: '🦈 Flair: +2 ATK si cible <50% HP',
        siren: '🧜 Chant: soigne alliés',
        bomber: '💥 Explosion: 2 dégâts zone à la mort',
        cursed: '🌊 Aura: -1 ATK joueurs proches',
        fast: '👻 Fantôme: traverse les alliés',
      };
      const passifLine = passifs[enemy.enemyType ?? ''] ?? '';
      showInfoPanel(this, `${def.emoji} ${enemy.name}`, [
        `HP: ${enemy.stats.hp}/${enemy.stats.maxHp}  ATK: ${enemy.stats.attack}  DEF: ${enemy.stats.defense}`,
        `→ ~${skillDmg} dégâts (${this.selectedSkill?.name ?? 'Attaque'})`,
        passifLine,
      ].filter(Boolean), { autoHideDelay: 4000 });
      return;
    }

    // Chercher un joueur sur cette case
    const player = this.players.find(p => p.isAlive && p.position.col === col && p.position.row === row);
    if (player) {
      const cls = CLASS_DEFINITIONS[this.getPlayerClass(player)];
      showInfoPanel(this, `${cls.emoji} ${player.name}`, [
        `HP: ${player.stats.hp}/${player.stats.maxHp}  ATK: ${player.stats.attack}  DEF: ${player.stats.defense}`,
        `PA: ${player.currentPa}/${player.stats.pa}  PM: ${player.currentPm}/${player.stats.pm}`,
        cls.passive,
      ], { autoHideDelay: 4000 });
    }
  }

  /** Nettoyage du TouchInputManager et des listeners à la destruction de la scène */
  shutdown(): void {
    this.touchInput?.destroy();
    // Retirer le listener longpress pour éviter les accumulations entre combats
    this.events.off('longpress');
  }
}
